<template>
  <v-container fluid class="page pt-3">
    <div class="page-hero">
      <div>
        <h2>{{ pageTitle }}</h2>
        <p>{{ pageSubtitle }}</p>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 8px;">
        <v-chip small color="primary" text-color="white" class="font-weight-bold">
          Lista activa: {{ activeListLabel }}
        </v-chip>
        <v-btn
          v-if="canExportCatalog"
          class="catalog-download-btn mr-1"
          small
          :loading="catalogExportLoading"
          :disabled="!catalogFilters.priceListId"
          @click="exportCatalogPriceList"
        >
          <v-icon left small>mdi-file-excel-outline</v-icon>
          Descargar XLSX
        </v-btn>
        <v-btn text small color="primary" :loading="bootstrapLoading" @click="bootstrap">Recargar</v-btn>
        <v-btn v-if="isAdmin && viewMode === 'pricing'" small color="success" @click="openImportDialog">
          <v-icon left small>mdi-upload</v-icon>
          Importar precios Excel
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="importDialog" max-width="860" persistent>
      <v-card outlined>
        <v-card-title class="subtitle-1">Importar lista de precios</v-card-title>
        <v-card-text>
          <v-alert v-if="importError" type="error" dense outlined class="mb-3">{{ importError }}</v-alert>
          <v-alert type="info" dense outlined class="mb-3">
            <strong>Cómo importar:</strong> descargá la plantilla, dejá los encabezados <strong>Código</strong> y <strong>Precio</strong>, y agregá un artículo por fila desde la fila 2.
            Las filas vacías y los códigos duplicados se omiten. Primero usá <strong>Previsualizar</strong>; sólo después se habilita <strong>Procesar</strong>.
            <div class="mt-1"><code>Código&nbsp;&nbsp;&nbsp;&nbsp; Precio</code><br><code>001.013&nbsp;&nbsp; 6593</code></div>
          </v-alert>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="importListId"
                :items="priceListOptions"
                item-text="label"
                item-value="id"
                label="Lista destino"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-file-input
                v-model="importFile"
                @change="importResult = null; importError = ''"
                label="Seleccioná el archivo completado (.csv, .xls o .xlsx)"
                outlined
                dense
                accept=".xlsx,.xls,.csv"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="importDescription" label="Descripción (opcional)" outlined dense />
            </v-col>
          </v-row>
          <div class="d-flex flex-wrap" style="gap: 8px;">
            <v-btn text @click="closeImportDialog">Cancelar</v-btn>
            <v-btn outlined color="primary" :loading="importTemplateLoading" :disabled="!importListId" @click="downloadImportTemplate">
              <v-icon left small>mdi-download-outline</v-icon>
              Descargar plantilla
            </v-btn>
            <v-btn color="primary" :loading="importLoading" :disabled="!canImportPreview" @click="runImportUpload(true)">Previsualizar</v-btn>
            <v-btn color="success" :loading="importLoading" :disabled="!canImportApply" @click="runImportUpload(false)">Procesar</v-btn>
          </div>
          <div v-if="importResult" class="mt-3">
            <v-alert :type="importResult.applied ? 'success' : 'info'" dense outlined class="mb-2">
              Filas con código: {{ importResult.totalRows }} | Procesadas: {{ importResult.processed }} | Creadas: {{ importResult.created }} | Actualizadas: {{ importResult.updated }} | Omitidas: {{ importResult.skipped }}
            </v-alert>
            <v-alert
              v-if="importResult.ignored && (importResult.ignored.emptyRows || importResult.ignored.withoutPriceRows || importResult.ignored.duplicateRows)"
              type="warning"
              dense
              outlined
              class="mb-2"
            >
              Se omitieron {{ importResult.ignored.emptyRows || 0 }} fila(s) vacía(s),
              {{ importResult.ignored.withoutPriceRows || 0 }} sin precio y
              {{ importResult.ignored.duplicateRows || 0 }} duplicada(s).
              <span v-if="importResult.ignored.duplicates && importResult.ignored.duplicates.length">
                Se conservó la primera aparición: fila {{ importResult.ignored.duplicates[0].row }} ({{ importResult.ignored.duplicates[0].code }}).
              </span>
            </v-alert>
            <v-data-table
              v-if="importResult.previewRows && importResult.previewRows.length"
              :headers="importPreviewHeaders"
              :items="importResult.previewRows.slice(0, 50)"
              dense
              disable-pagination
              hide-default-footer
              class="mt-2"
            >
              <template v-slot:item.currentPrice="{ item }">{{ formatNumber(item.currentPrice) }}</template>
              <template v-slot:item.newPrice="{ item }">{{ formatNumber(item.newPrice) }}</template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-alert v-if="error" type="error" dense outlined dismissible class="mb-4" @input="error = ''">{{ error }}</v-alert>
    <v-alert v-if="info" type="info" dense outlined class="mb-4">{{ info }}</v-alert>

    <v-card outlined class="page-card">
      <v-tabs v-model="tab" background-color="transparent">
        <v-tab v-if="canShowCatalogTab">Catálogo mostrador</v-tab>
        <v-tab v-if="canShowUpdatesTab">Actualizaciones</v-tab>
        <v-tab v-if="canShowProvidersTab">Proveedores y reglas</v-tab>
        <v-tab v-if="canShowStockTab">Stock</v-tab>
        <v-tab v-if="canShowAuditTab">Auditoría</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="mt-4">
        <v-tab-item v-if="canShowCatalogTab">
          <v-row dense class="catalog-toolbar">
            <v-col cols="12" md="3">
              <v-select
                v-model="catalogFilters.priceListId"
                :items="priceListOptions"
                item-text="label"
                item-value="id"
                label="Lista de precio"
                outlined
                dense
                hide-details="auto"
                @change="fetchCatalog"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="catalogFilters.suggestedProductCode"
                :search-input.sync="catalogFilters.searchText"
                :items="catalogSuggestions"
                :loading="catalogSuggestLoading"
                item-text="label"
                item-value="code"
                clearable
                hide-no-data
                label="Buscar artículo (código o nombre, predictivo)"
                outlined
                dense
                hide-details="auto"
                @update:search-input="onCatalogSearchInput"
                @change="onCatalogOptionSelected"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-end">
              <v-btn color="primary" block :loading="catalogLoading" @click="fetchCatalog" style="height: 40px;">Buscar</v-btn>
            </v-col>
          </v-row>

          <v-expansion-panels v-if="isAdmin" flat class="mt-1">
            <v-expansion-panel>
              <v-expansion-panel-header>Filtros avanzados (admin)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="3"><v-select v-model="catalogFilters.providerId" :items="providerOptions" item-text="label" item-value="id" clearable label="Proveedor" outlined dense /></v-col>
                  <v-col cols="12" md="3"><v-select v-model="catalogFilters.productStatus" :items="productStatusOptions" item-text="text" item-value="value" label="Estado artículo" outlined dense /></v-col>
                  <v-col cols="12" md="3"><v-text-field v-model="catalogFilters.codeFrom" label="Código desde" outlined dense /></v-col>
                  <v-col cols="12" md="3"><v-text-field v-model="catalogFilters.codeTo" label="Código hasta" outlined dense /></v-col>
                  <v-col cols="12" md="3"><v-switch v-model="catalogFilters.includeZero" inset label="Incluye cero" /></v-col>
                  <v-col cols="12" md="3"><v-switch v-model="catalogShowAdvancedCols" inset label="Ver columnas técnicas" /></v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-data-table 
            class="mt-3 catalog-table" 
            :key="catalogHeadersView.map(h => h.value).join(',')"
            :headers="catalogHeadersView" 
            :items="catalogRows" 
            :loading="catalogLoading" 
            item-key="productId" 
            :mobile-breakpoint="960"
            dense
            :footer-props="{
              'items-per-page-options': [20, 50, 100, -1],
              'items-per-page-text': 'Filas por página:'
            }"
          >
            <!-- Forzar nombre de cabecera dinámico -->
            <template v-for="disc in currentListMatrixDiscounts" v-slot:[`header.price_matrix_${disc}`]>
              <span :key="'hd_' + disc" class="text-uppercase font-weight-bold">
                Lista -{{ disc }}%
              </span>
            </template>

            <template v-slot:item.imagePath="{ item }">
              <v-avatar size="40" tile class="rounded-lg elevation-1 catalog-avatar-interactive" style="cursor: pointer;" @click="zoomImage(item)">
                <img v-if="item.imagePath" :src="resolveImageUrl(item.imagePath)" alt="img" style="object-fit: cover; width: 100%; height: 100%;" />
                <v-icon v-else color="grey lighten-1" small>mdi-image-off-outline</v-icon>
              </v-avatar>
            </template>
            <template v-slot:item.name="{ item }">
              <div class="catalog-description">{{ item.name }}</div>
            </template>
            <template v-slot:item.formattedPrice="{ item }">{{ item.formattedPrice }}</template>
            <template v-slot:item.formattedFinalPrice="{ item }">
              <div v-if="item.appliedBonus">
                <div class="caption grey--text text--darken-1">Precio según lista: {{ item.formattedPrice }}</div>
                <div class="caption success--text font-weight-bold">Ahorro adicional {{ bonusLabel(item) }} (-$ {{ formatCurrency(item.bonusAmount) }})</div>
                <div class="font-weight-bold">Precio final: {{ item.formattedFinalPrice }}</div>
              </div>
              <div v-else>{{ item.formattedFinalPrice }}</div>
            </template>
            <template v-slot:item.providerCostPrice="{ item }">{{ formatNullableNumber(item.providerCostPrice) }}</template>
            <template v-slot:item.bonusAmount="{ item }">{{ formatNumber(item.bonusAmount) }}</template>
            <template v-slot:item.stockAvailable="{ item }">
              <v-chip
                small
                class="font-weight-bold"
                :color="Number(item.stockAvailable) <= 0 ? 'red lighten-5' : 'green lighten-5'"
                :text-color="Number(item.stockAvailable) <= 0 ? 'red darken-3' : 'green darken-3'"
                style="min-width: 60px; justify-content: center;"
              >
                <v-icon x-small class="mr-1" :color="Number(item.stockAvailable) <= 0 ? 'red' : 'green'">
                  {{ Number(item.stockAvailable) <= 0 ? 'mdi-close-circle' : 'mdi-check-circle' }}
                </v-icon>
                {{ formatInteger(item.stockAvailable) }}
              </v-chip>
            </template>
            <template v-slot:item.status="{ item }">{{ statusLabel(item.status) }}</template>
            <template v-slot:item.canSell="{ item }">{{ item.canSell ? "Sí" : "No" }}</template>
            <template v-slot:item.lastPriceChangeAt="{ item }">{{ formatDateTime(item.lastPriceChangeAt) }}</template>
            
            <template v-slot:item.cartAction="{ item }">
              <v-btn
                icon
                color="primary"
                :disabled="Number(item.stockAvailable) <= 0"
                :title="Number(item.stockAvailable) <= 0 ? 'Sin stock disponible' : 'Agregar al carrito'"
                @click="addToCart(item)"
              >
                <v-icon>mdi-cart-plus</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-alert
            v-if="!catalogLoading && catalogRows.length === 0 && catalogFilters.priceListId"
            type="warning"
            dense
            outlined
            class="mt-3"
          >
            La lista seleccionada no tiene precios cargados. Cambia de lista para ver resultados.
          </v-alert>
        </v-tab-item>

        <v-tab-item v-if="canShowUpdatesTab">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Ajuste rápido por proveedor</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="4"><v-select v-model="byProviderForm.priceListId" :items="priceListOptions" item-text="label" item-value="id" label="Lista" outlined dense @change="onByProviderListChanged" /></v-col>
                    <v-col cols="12" md="4"><v-autocomplete v-model="byProviderForm.providerId" :items="providerOptions" item-text="label" item-value="id" label="Proveedor" outlined dense @change="onByProviderProviderChanged" /></v-col>
                    <v-col cols="12" md="4">
                      <v-btn-toggle v-model="byProviderForm.updateMethod" mandatory dense class="mt-1">
                        <v-btn value="percent" small>Subir/Bajar %</v-btn>
                        <v-btn value="cost_coef" small>Costo x coeficiente</v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>

                  <v-row dense v-if="byProviderForm.updateMethod === 'percent'">
                    <v-col cols="12" md="6" v-if="byProviderForm.updateMethod === 'percent'">
                      <v-btn-toggle v-model="byProviderForm.changeType" mandatory dense class="mt-1">
                        <v-btn value="increase" small>Aumentar</v-btn>
                        <v-btn value="decrease" small>Disminuir</v-btn>
                      </v-btn-toggle>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model.number="byProviderForm.percent"
                        type="number"
                        min="0"
                        step="0.01"
                        label="Porcentaje (%)"
                        outlined
                        dense
                      />
                    </v-col>
                  </v-row>

                  <v-row dense v-else>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="byProviderForm.coefficientSource"
                        :items="coefficientSourceOptions"
                        item-text="text"
                        item-value="value"
                        label="Coeficiente"
                        outlined
                        dense
                      />
                    </v-col>
                    <v-col cols="12" md="6" v-if="byProviderForm.coefficientSource === 'fixed'">
                      <v-text-field
                        v-model.number="byProviderForm.fixedCoefficient"
                        type="number"
                        min="0.0001"
                        step="0.0001"
                        label="Coeficiente fijo"
                        outlined
                        dense
                      />
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-combobox
                        v-model="byProviderForm.rubro"
                        :items="byProviderRubroOptions"
                        :loading="byProviderRubroLoading"
                        item-text="label"
                        item-value="value"
                        clearable
                        label="Rubro"
                        outlined
                        dense
                      />
                    </v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="byProviderForm.codeFrom" label="Código desde" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="byProviderForm.codeTo" label="Código hasta" outlined dense /></v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="12">
                      <v-autocomplete
                        v-model="byProviderSelectedSuggestion"
                        :search-input.sync="byProviderForm.searchText"
                        :items="byProviderSuggestions"
                        :loading="byProviderSuggestLoading"
                        item-text="label"
                        return-object
                        clearable
                        hide-no-data
                        label="Buscar artículo puntual (predictivo, opcional)"
                        outlined
                        dense
                        @update:search-input="onByProviderSearchInput"
                        @change="onByProviderOptionSelected"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="12">
                      <v-text-field
                        v-model="byProviderForm.productCodesCsv"
                        label="Códigos exactos (coma, opcional)"
                        hint="Puedes combinar proveedor + rubro + código puntual"
                        persistent-hint
                        outlined
                        dense
                      />
                    </v-col>
                  </v-row>
                  <v-alert type="info" dense outlined class="mb-2">
                    Se aplicará a los artículos filtrados del proveedor en la lista seleccionada.
                  </v-alert>
                  <v-alert type="info" dense outlined class="mb-2">
                    {{ byProviderScopeLabel }}
                  </v-alert>
                  <v-alert type="info" dense outlined class="mb-2">
                    Bonificaciones especiales se aplican después, al consultar/vender (cliente y cantidad), no en esta actualización masiva.
                  </v-alert>
                  <div class="actions-row">
                    <v-btn color="primary" :loading="byProviderLoading" @click="previewByProvider">Ver artículos alcanzados</v-btn>
                    <v-btn color="success" :loading="byProviderApplyLoading" @click="requestMutationConfirm('applyByProvider')">Aplicar</v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>Opciones avanzadas (técnico)</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-card outlined class="inner-card mb-4">
                      <v-card-title class="subtitle-1">Actualización masiva (avanzado)</v-card-title>
                      <v-card-text>
                        <v-row dense>
                          <v-col cols="12" md="4"><v-select v-model="bulkForm.priceListId" :items="priceListOptions" item-text="label" item-value="id" label="Lista" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-select v-model="bulkForm.mode" :items="modeOptions" item-text="text" item-value="value" label="Modo técnico" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-text-field v-model.number="bulkForm.value" type="number" label="Valor" outlined dense /></v-col>
                          <v-col cols="12" md="2"><v-select v-model="bulkForm.formulaId" :items="formulaOptions" item-text="label" item-value="id" clearable label="Fórmula" no-data-text="No hay fórmulas activas" outlined dense /></v-col>
                        </v-row>
                        <v-text-field v-model="bulkForm.expression" label="Expresión (solo técnico)" outlined dense class="mt-1" />
                        <v-row dense>
                          <v-col cols="12" md="3"><v-text-field v-model="bulkForm.rubro" label="Rubro" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-text-field v-model="bulkForm.subrubro" label="Subrubro" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-select v-model="bulkForm.providerId" :items="providerOptions" item-text="label" item-value="id" clearable label="Proveedor" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-select v-model="bulkForm.isProductActive" :items="productActiveOptions" label="Producto activo" outlined dense /></v-col>
                        </v-row>
                        <v-text-field v-model="bulkForm.productCodesCsv" label="Códigos producto (coma)" outlined dense />
                        <v-switch v-model="bulkForm.affectDependencies" inset label="Aplicar cascada a listas dependientes" />
                        <div class="actions-row">
                          <v-btn color="primary" :loading="bulkPreviewLoading" @click="previewBulk">Previsualizar</v-btn>
                          <v-btn color="success" :loading="bulkApplyLoading" @click="requestMutationConfirm('applyBulk')">Aplicar</v-btn>
                        </div>
                      </v-card-text>
                    </v-card>

                    <v-card outlined class="inner-card">
                      <v-card-title class="subtitle-1">Actualización por campo (avanzado)</v-card-title>
                      <v-card-text>
                        <v-row dense>
                          <v-col cols="12" md="4"><v-select v-model="byFieldForm.priceListId" :items="priceListOptions" item-text="label" item-value="id" label="Lista" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-select v-model="byFieldForm.targetField" :items="fieldOptions" label="Campo" outlined dense /></v-col>
                          <v-col cols="12" md="3"><v-select v-model="byFieldForm.operation" :items="operationOptions" label="Operación" outlined dense /></v-col>
                          <v-col cols="12" md="2"><v-text-field v-model.number="byFieldForm.value" type="number" label="Valor" outlined dense /></v-col>
                        </v-row>
                        <v-text-field v-model="byFieldForm.productIdsCsv" label="Product IDs (coma, opcional)" outlined dense />
                        <v-text-field v-model="byFieldForm.productCodesCsv" label="Códigos de artículo (coma, opcional)" outlined dense />
                        <v-btn color="primary" :loading="byFieldLoading" @click="requestMutationConfirm('applyByField')">Aplicar por campo</v-btn>
                      </v-card-text>
                    </v-card>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-card outlined class="inner-card mt-4" v-if="bulkSummary || byProviderSummary || byFieldResult">
                <v-card-title class="subtitle-1">Resumen</v-card-title>
                <v-card-text>
                  <div v-if="bulkSummary"><strong>Masivo:</strong> {{ bulkSummary }}</div>
                  <div v-if="byProviderSummary" class="mt-2"><strong>Por proveedor:</strong> {{ byProviderSummary }}</div>
                  <div v-if="byFieldResult" class="mt-2"><strong>Por campo:</strong> {{ byFieldResult }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-data-table ref="previewTable" class="mt-4" :headers="previewHeaders" :items="previewRows" :loading="bulkPreviewLoading || byProviderLoading" item-key="productId" dense>
            <template v-slot:item.previousPrice="{ item }">{{ formatNumber(item.previousPrice) }}</template>
            <template v-slot:item.newPrice="{ item }">{{ formatNumber(item.newPrice) }}</template>
            <template v-slot:item.changePercent="{ item }">{{ item.changePercent === null || item.changePercent === undefined ? "—" : `${formatNumber(item.changePercent)} %` }}</template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item v-if="canShowProvidersTab">
          <v-row>
            <v-col cols="12" lg="6">
              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Catálogo de proveedores</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="5"><v-text-field v-model="providerFilters.q" label="Buscar" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-select v-model="providerFilters.isActive" :items="activeOptions" label="Estado" outlined dense /></v-col>
                    <v-col cols="12" md="3" class="d-flex align-end"><v-btn color="primary" block :loading="providersLoading" @click="fetchProviders">Buscar</v-btn></v-col>
                  </v-row>
                  <v-divider class="my-2" />
                  <v-row dense>
                    <v-col cols="12" md="4"><v-text-field v-model="providerForm.code" label="Código" outlined dense /></v-col>
                    <v-col cols="12" md="5"><v-text-field v-model="providerForm.name" label="Nombre" outlined dense /></v-col>
                    <v-col cols="12" md="3"><v-btn color="success" block :loading="providerSaveLoading" @click="requestMutationConfirm('saveProvider')">{{ providerForm.id ? 'Actualizar' : 'Crear' }}</v-btn></v-col>
                    <v-col cols="12"><v-text-field v-model="providerForm.description" label="Descripción" outlined dense /></v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <v-data-table :headers="providerHeaders" :items="providers" item-key="id" dense>
                <template v-slot:item.isActive="{ item }"><span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">{{ item.isActive ? 'Activo' : 'Inactivo' }}</span></template>
                <template v-slot:item.actions="{ item }"><v-btn text small color="primary" @click="editProvider(item)">Editar</v-btn></template>
              </v-data-table>

              <v-card outlined class="inner-card mt-4">
                <v-card-title class="subtitle-1">Rubros existentes</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="8"><v-text-field v-model="rubroCatalogFilters.q" label="Buscar rubro" outlined dense /></v-col>
                    <v-col cols="12" md="4" class="d-flex align-end"><v-btn color="primary" block :loading="rubroCatalogLoading" @click="fetchRubroCatalog">Actualizar</v-btn></v-col>
                  </v-row>
                  <v-data-table class="mt-2" :headers="rubroCatalogHeaders" :items="rubroCatalog" item-key="rubro" dense :loading="rubroCatalogLoading" />
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" lg="6">
              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Regla aplicada por lista</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="listRuleForm.priceListId"
                        :items="priceListOptions"
                        item-text="label"
                        item-value="id"
                        label="Lista de precios"
                        outlined
                        dense
                        @change="syncListRuleFromList"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="listRuleForm.formulaId"
                        :items="formulaOptions"
                        item-text="label"
                        item-value="id"
                        clearable
                        label="Regla a aplicar"
                        outlined
                        dense
                      />
                    </v-col>
                  </v-row>
                  <v-alert type="info" dense outlined class="mb-3">
                    Regla actual: <strong>{{ currentListRuleLabel }}</strong>
                  </v-alert>
                  <div class="actions-row">
                    <v-btn color="success" :loading="listRuleSaving" @click="requestMutationConfirm('linkRuleToList')">Vincular regla</v-btn>
                    <v-btn text color="error" :loading="listRuleSaving" @click="requestMutationConfirm('unlinkRuleFromList')">Quitar regla</v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Reglas de precio</v-card-title>
                <v-card-text>
                  <v-alert type="info" dense outlined class="mb-3">
                    Definí si querés incrementar o decrementar y el porcentaje. El sistema aplica eso en forma automática.
                  </v-alert>
                  <v-row dense>
                    <v-col cols="12" md="5"><v-text-field v-model="formulaFilters.q" label="Buscar regla" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-select v-model="formulaFilters.isActive" :items="activeOptions" label="Estado" outlined dense /></v-col>
                    <v-col cols="12" md="3" class="d-flex align-end"><v-btn color="primary" block :loading="formulasLoading" @click="fetchFormulas">Buscar</v-btn></v-col>
                  </v-row>
                  <v-divider class="my-2" />
                  <div class="actions-row">
                    <v-btn color="success" @click="openCreateFormulaDialog">Nueva regla</v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <v-data-table :headers="formulaHeaders" :items="formulas" item-key="id" dense>
                <template v-slot:item.isActive="{ item }"><span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">{{ item.isActive ? 'Activa' : 'Inactiva' }}</span></template>
                <template v-slot:item.expression="{ item }">{{ describeExpression(item.expression) }}</template>
                <template v-slot:item.actions="{ item }"><v-btn text small color="primary" @click="editFormula(item)">Editar</v-btn></template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item v-if="canShowStockTab">
          <v-row>
            <v-col cols="12" lg="6">
              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Consulta masiva de stock</v-card-title>
                <v-card-text>
                  <v-text-field v-model="stockQueryForm.productCodesCsv" label="Códigos producto (coma)" outlined dense />
                  <v-text-field v-model="stockQueryForm.warehouseCodesCsv" label="Códigos depósito (coma)" outlined dense />
                  <v-switch v-model="stockQueryForm.includeZero" inset label="Incluir stock cero" />
                  <v-btn color="primary" :loading="stockQueryLoading" @click="runStockQuery">Consultar stock</v-btn>
                </v-card-text>
              </v-card>

              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Ajuste masivo de stock</v-card-title>
                <v-card-text>
                  <v-textarea v-model="stockAdjustJson" label="JSON { adjustments: [...] }" rows="8" auto-grow outlined dense />
                  <v-btn color="success" :loading="stockAdjustLoading" @click="requestMutationConfirm('runStockAdjust')">Aplicar ajustes</v-btn>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" lg="6">
              <v-card outlined class="inner-card mb-4">
                <v-card-title class="subtitle-1">Movimientos de stock</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="4"><v-text-field v-model="movementFilters.productCode" label="Producto" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="movementFilters.warehouseCode" label="Depósito" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="movementFilters.movementType" label="Tipo" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="movementFilters.dateFrom" type="date" label="Desde" outlined dense /></v-col>
                    <v-col cols="12" md="4"><v-text-field v-model="movementFilters.dateTo" type="date" label="Hasta" outlined dense /></v-col>
                    <v-col cols="12" md="4" class="d-flex align-end"><v-btn color="primary" block :loading="movementsLoading" @click="fetchMovements">Buscar</v-btn></v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <div v-if="stockAdjustResult" class="mb-2"><strong>Resultado ajuste:</strong> {{ stockAdjustResult }}</div>
            </v-col>
          </v-row>

          <v-data-table class="mb-4" :headers="stockHeaders" :items="stockRows" item-key="stockId" dense />
          <v-data-table :headers="movementHeaders" :items="stockMovements" item-key="id" dense>
            <template v-slot:item.createdAt="{ item }">{{ formatDateTime(item.createdAt) }}</template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item v-if="canShowAuditTab">
          <v-card outlined class="inner-card mb-4">
            <v-card-title class="subtitle-1">Filtros auditoría</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="3"><v-text-field v-model="auditFilters.resourceType" label="resourceType" outlined dense /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="auditFilters.action" label="action" outlined dense /></v-col>
                <v-col cols="12" md="2"><v-select v-model="auditFilters.success" :items="booleanAnyOptions" label="success" outlined dense /></v-col>
                <v-col cols="12" md="2"><v-text-field v-model="auditFilters.dateFrom" type="date" label="Desde" outlined dense /></v-col>
                <v-col cols="12" md="2"><v-text-field v-model="auditFilters.dateTo" type="date" label="Hasta" outlined dense /></v-col>
              </v-row>
              <v-btn color="primary" :loading="auditLoading" @click="fetchAuditLogs">Consultar auditoría</v-btn>
            </v-card-text>
          </v-card>

          <v-data-table :headers="auditHeaders" :items="auditLogs" item-key="id" dense>
            <template v-slot:item.success="{ item }"><span :class="['pill', item.success ? 'pill--success' : 'pill--warn']">{{ item.success ? 'OK' : 'Error' }}</span></template>
            <template v-slot:item.createdAt="{ item }">{{ formatDateTime(item.createdAt) }}</template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Dialog para agrandar y previsualizar la imagen del producto -->
    <v-dialog v-model="zoomImageDialog" max-width="600">
      <v-card class="premium-zoom-card pa-1" style="border-radius: 16px; overflow: hidden; background: #ffffff;">
        <v-card-title class="subtitle-1 d-flex align-center justify-space-between pb-2 pt-3 px-4" style="border-bottom: 1px solid #f0f0f0;">
          <div class="d-flex flex-column text-left">
            <span class="font-weight-bold primary--text subtitle-2" style="letter-spacing: 0.5px;">CÓDIGO: {{ zoomProductCode }}</span>
            <span class="body-2 grey--text text--darken-3 font-weight-medium text-truncate" style="max-width: 420px;">{{ zoomProductName }}</span>
          </div>
          <v-btn icon small @click="zoomImageDialog = false" class="grey lighten-4">
            <v-icon small color="grey darken-2">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="d-flex justify-center align-center pa-4" style="background: #fafafa; min-height: 300px;">
          <v-img
            :src="zoomImageUrl"
            max-height="450"
            contain
            class="rounded-lg elevation-1"
            style="background: white; border: 1px solid #e0e0e0;"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="primary" />
              </v-row>
            </template>
          </v-img>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="mutationConfirmDialog" max-width="520">
      <v-card>
        <v-card-title class="subtitle-1">Confirmar modificación</v-card-title>
        <v-card-text>
          ¿Confirmas ejecutar esta acción?
          <div class="mt-2"><strong>{{ mutationConfirmLabel }}</strong></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="mutationConfirmLoading" @click="mutationConfirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="mutationConfirmLoading" @click="confirmMutation">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="formulaDialog" max-width="760">
      <v-card>
        <v-card-title class="subtitle-1">{{ formulaForm.id ? "Editar regla" : "Nueva regla" }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="4"><v-text-field v-model="formulaForm.code" label="Código" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="formulaForm.name" label="Nombre de regla" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-select v-model="formulaPercentForm.operation" :items="ruleOperationOptions" item-text="text" item-value="value" label="Acción" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model.number="formulaPercentForm.percent" type="number" min="0" step="0.01" label="Porcentaje (%)" outlined dense /></v-col>
            <v-col cols="12" md="8"><v-text-field :value="formulaPercentPreview" label="Cómo se aplicará" outlined dense readonly /></v-col>
            <v-col cols="12"><v-text-field v-model="formulaForm.notes" label="Observaciones" outlined dense /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="formulaSaveLoading" @click="closeFormulaDialog">Cancelar</v-btn>
          <v-btn color="success" :loading="formulaSaveLoading" @click="requestMutationConfirm('saveFormula')">
            {{ formulaForm.id ? "Actualizar" : "Crear" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="mutationSuccessDialog" max-width="520">
      <v-card>
        <v-card-title class="subtitle-1">Operación exitosa</v-card-title>
        <v-card-text>{{ mutationSuccessMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="mutationSuccessDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-navigation-drawer v-model="cartDrawer" right fixed temporary width="460" class="cart-drawer">
      <div class="cart-content">
        <div class="cart-header">
          <div>
            <div class="cart-eyebrow">VENTA ACTUAL</div>
            <h3>Venta mostrador</h3>
            <div v-if="cart.length" class="cart-summary">{{ cart.length }} {{ cart.length === 1 ? 'artículo' : 'artículos' }} · {{ cart.reduce((total, item) => total + Number(item.quantity || 0), 0) }} unidades</div>
          </div>
          <v-btn icon @click="cartDrawer = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="cart-items" v-if="cart.length > 0">
          <div v-for="(item, idx) in cart" :key="idx" class="cart-item">
            <div class="d-flex align-center w-100">
              <div class="cart-qty-wrapper flex-shrink-0">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  dense
                  outlined
                  hide-details
                  class="qty-input-compact"
                  @change="normalizeCartQuantity(item)"
                />
              </div>
              
              <div class="cart-info-wrapper flex-grow-1 px-2 text-left overflow-hidden">
                <div class="cart-item-code-nano primary--text font-weight-bold">{{ item.code }}</div>
                <div class="cart-item-name-nano grey--text text--darken-4">{{ item.name }}</div>
                <div v-if="item.appliedBonus" class="caption success--text font-weight-bold">Extra {{ bonusLabel(item) }}</div>
              </div>
              
              <div class="cart-action-wrapper d-flex align-center justify-end flex-shrink-0">
                <div class="cart-item-price-nano font-weight-bold mr-2 black--text">
                  $ {{ formatCurrency(item.finalPrice * item.quantity) }}
                </div>
                <v-btn icon color="red lighten-2" x-small @click="removeFromCart(idx)" class="ma-0">
                  <v-icon small>mdi-close-circle-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-cart">
          <v-icon size="48" color="grey lighten-1">mdi-cart-outline</v-icon>
          <p>El carrito está vacío</p>
        </div>

        <div class="cart-footer" v-if="cart.length > 0">
          <div class="cart-section-title">Cliente</div>
          <v-combobox
            v-model="customerSearchVal"
            :items="customerSuggestions"
            :loading="customerSearchLoading"
            :search-input.sync="customerSearchQuery"
            item-text="name"
            item-value="name"
            label="Nombre del Cliente (Obligatorio)"
            outlined
            dense
            prepend-inner-icon="mdi-account"
            class="mb-2"
            hide-details
            no-filter
            clearable
            placeholder="Buscar cliente o escribir uno nuevo..."
            :error-messages="customerError"
            @change="onCustomerSelected"
          />
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="customerTaxId"
                label="CUIT / DNI"
                outlined
                dense
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="customerTaxCondition"
                :items="['Consumidor Final', 'Responsable Inscripto', 'Monotributista', 'Exento']"
                label="Condición IVA"
                outlined
                dense
                hide-details
                class="mb-2"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="customerEmail"
                label="Email (Opcional)"
                outlined
                dense
                hide-details
                class="mb-2"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="customerPhone"
                label="Teléfono (Opcional)"
                outlined
                dense
                hide-details
                class="mb-2"
              />
            </v-col>
          </v-row>

          <div v-if="currentListMatrixDiscounts.length > 0" class="mb-3 mt-2 px-1">
            <div class="caption grey--text text--darken-2 mb-1 font-weight-bold">Aplicar Descuento al Total</div>
            <div class="d-flex justify-space-between border rounded pa-1" style="width: 100%; background: #f5f5f5; gap: 4px;">
              <!-- Botón 0% -->
              <v-btn 
                x-small 
                depressed
                height="24"
                style="min-width: 0; font-size: 10px !important;"
                class="flex-grow-1 font-weight-bold"
                :class="cartGlobalDiscount === 0 ? 'grey darken-1 white--text' : 'white grey--text text--darken-2'"
                @click="selectCartDiscount(0)"
              >
                0%
              </v-btn>
              <!-- Botones dinámicos -->
              <v-btn 
                v-for="disc in currentListMatrixDiscounts" 
                :key="disc" 
                x-small 
                depressed
                height="24"
                style="min-width: 0; font-size: 10px !important;"
                class="flex-grow-1 font-weight-bold"
                :class="cartGlobalDiscount === disc ? 'orange lighten-4 orange--text text--darken-4' : 'white grey--text text--darken-2'"
                @click="selectCartDiscount(disc)"
              >
                -{{ disc }}%
              </v-btn>
            </div>
          </div>

          <div class="cart-checkout" :key="cartTotal">
            <div>
              <div class="cart-total-label">Total a pagar</div>
              <div class="cart-total-hint">La forma de pago se elige en caja</div>
            </div>
            <span class="total-amount">$ {{ formatCurrency(cartTotal) }}</span>
          </div>
          <v-btn 
            color="primary"
            block
            large
            class="mb-2"
            :loading="savingOrder"
            @click="saveCartOrder"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            GUARDAR PEDIDO
          </v-btn>
          <v-btn 
            color="success" 
            block 
            large 
            class="cart-checkout-button" 
            :loading="sendingToCashier"
            @click="processSendToCashier"
          >
            <v-icon left>mdi-cash-register</v-icon>
            PASAR A CAJA
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-btn 
      v-if="cart.length > 0" 
      fab 
      fixed 
      bottom 
      right 
      color="primary" 
      class="cart-fab"
      @click="cartDrawer = true"
    >
      <v-badge overlap color="error" :content="cart.length">
        <v-icon>mdi-cart</v-icon>
      </v-badge>
    </v-btn>

    <!-- Diálogo Premium de Error / Validación de Stock -->
    <v-dialog v-model="errorDialog" max-width="500">
      <v-card style="border-top: 5px solid #ff5252; border-radius: 12px; overflow: hidden;">
        <v-card-title class="headline red--text text--darken-1 d-flex align-center pb-3">
          <v-icon color="red" class="mr-2" large>mdi-alert-circle</v-icon>
          <span class="font-weight-bold" style="font-size: 18px;">Orden Rechazada por Falta de Stock</span>
        </v-card-title>
        <v-card-text class="pt-2">
          <p class="grey--text text--darken-3 mb-3" style="font-size: 14px; line-height: 1.5;">
            El sistema de inventario rechazó la reserva de stock para este pedido debido a unidades insuficientes:
          </p>
          <v-alert type="error" text class="mb-0 font-weight-bold" style="font-size: 14px; border-radius: 8px; line-height: 1.5;">
            {{ activeErrorMsg }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="grey lighten-5 px-4 py-3">
          <v-spacer />
          <v-btn color="red" class="px-6 white--text rounded-pill font-weight-bold elevation-1" @click="errorDialog = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx-js-style";
import { priceListsService } from "../services/priceLists";
import { pricingStockService } from "../services/pricingStock";
import { salesService } from "../services/sales";
import api from "../services/api";

function splitCsv(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

function parseJsonText(raw) {
  if (!raw || !raw.trim()) return null;
  return JSON.parse(raw);
}

export default {
  name: "SalesPriceList",
  props: {
    viewMode: {
      type: String,
      default: "full",
    },
  },
  data() {
    return {
      tab: 0,
      bootstrapLoading: false,
      error: "",
      errorDialog: false,
      activeErrorMsg: "",
      info: "",
      success: "",
      priceLists: [],
      providers: [],
      formulas: [],
      cart: [],
      cartDrawer: false,
      cartGlobalDiscount: 0,
      customerSearchVal: null,
      customerSearchQuery: null,
      customerSuggestions: [],
      customerSearchLoading: false,
      customerSearchDebounce: null,
      customerName: "",
      customerTaxId: "",
      customerTaxCondition: "Consumidor Final",
      customerEmail: "",
      customerPhone: "",
      customerError: "",
      sendingToCashier: false,
      savingOrder: false,
      zoomImageDialog: false,
      zoomImageUrl: "",
      zoomProductCode: "",
      zoomProductName: "",

      catalogLoading: false,
      catalogExportLoading: false,
      importTemplateLoading: false,
      catalogSuggestLoading: false,
      catalogRows: [],
      catalogSuggestions: [],
      catalogShowAdvancedCols: false,
      catalogHeaders: [
        { text: "Img", value: "imagePath", sortable: false },
        { text: "Código", value: "code" },
        { text: "Descripción", value: "name", align: "left" },
        { text: "Unidad", value: "saleUnit" },
        { text: "Envase", value: "packageUnit" },
        { text: "Precio", value: "formattedPrice" },
        { text: "Precio final", value: "formattedFinalPrice" },
        { text: "Bonif", value: "bonusAmount" },
        { text: "Coef. utilidad", value: "utilityCoefficient" },
        { text: "Costo proveedor", value: "providerCostPrice" },
        { text: "Proveedor", value: "providerName" },
        { text: "Desc proveedor", value: "providerDescription" },
        { text: "Cod fabricante", value: "manufacturerCode" },
        { text: "Stock", value: "stockAvailable" },
        { text: "Estado", value: "status" },
        { text: "Vendible", value: "canSell" },
        { text: "Último cambio", value: "lastPriceChangeAt" },
        { text: "", value: "cartAction", sortable: false, align: "end" },
      ],
      catalogHeadersSeller: [
        { text: "Img", value: "imagePath", sortable: false },
        { text: "Código", value: "code" },
        { text: "Descripción", value: "name", align: "left" },
        { text: "Precio final", value: "formattedFinalPrice" },
        { text: "Último cambio", value: "lastPriceChangeAt" },
        { text: "", value: "cartAction", sortable: false, align: "end" },
      ],
      catalogFilters: {
        priceListId: "",
        suggestedProductCode: "",
        searchText: "",
        providerId: "",
        customerType: "all",
        quantity: 1,
        code: "",
        codeFrom: "",
        codeTo: "",
        priceMin: null,
        priceMax: null,
        material: "",
        q: "",
        sortBy: "code",
        sortDir: "asc",
        includeZero: true,
        productStatus: "all",
        exactSelected: false,
      },
      catalogSearchDebounce: null,

      effectiveForm: { priceListId: "", productCode: "", providerId: "", atDate: "" },
      effectiveLoading: false,
      effectiveResult: null,

      bulkForm: { priceListId: "", mode: "multiplier", value: null, formulaId: "", expression: "", providerId: "", rubro: "", subrubro: "", isProductActive: "all", productCodesCsv: "", affectDependencies: false },
      byProviderForm: {
        priceListId: "",
        providerId: "",
        updateMethod: "percent",
        changeType: "increase",
        percent: null,
        coefficientSource: "product",
        fixedCoefficient: null,
        rubro: "",
        codeFrom: "",
        codeTo: "",
        productCodesCsv: "",
        suggestedProductCode: "",
        searchText: "",
      },
      byProviderSuggestions: [],
      byProviderSelectedSuggestion: null,
      byProviderSuggestLoading: false,
      byProviderRubroOptions: [],
      byProviderRubroLoading: false,
      byFieldForm: { priceListId: "", targetField: "price", operation: "set", value: null, productIdsCsv: "", productCodesCsv: "" },
      bulkPreviewLoading: false,
      bulkApplyLoading: false,
      byProviderLoading: false,
      byProviderApplyLoading: false,
      byFieldLoading: false,
      previewRows: [],
      bulkSummary: "",
      byProviderSummary: "",
      byFieldResult: "",

      providerFilters: { q: "", isActive: "all" },
      providersLoading: false,
      providerSaveLoading: false,
      providerForm: { id: "", code: "", name: "", description: "", isActive: true },
      rubroCatalogLoading: false,
      rubroCatalogFilters: { q: "" },
      rubroCatalog: [],

      formulaFilters: { q: "", isActive: "all" },
      formulasLoading: false,
      formulaSaveLoading: false,
      formulaDialog: false,
      formulaForm: { id: "", code: "", name: "", expression: "PRICE", version: 1, notes: "", isActive: true },
      formulaPercentForm: { operation: "increase", percent: 10 },
      ruleOperationOptions: [
        { text: "Incrementar", value: "increase" },
        { text: "Decrementar", value: "decrease" },
      ],
      listRuleForm: { priceListId: "", formulaId: "" },
      listRuleSaving: false,

      bonusLoading: false,
      bonusUpsertLoading: false,
      bonusUpsertJson: '{\n  "items": [\n    {\n      "productCode": "ABC123",\n      "priceListCode": "B",\n      "customerType": "final_consumer",\n      "minQuantity": 1,\n      "bonusType": "percent",\n      "bonusValue": 5\n    }\n  ]\n}',

      memberships: [],
      membershipsLoading: false,
      membershipUpsertLoading: false,
      membershipFilters: { priceListId: "", productCode: "" },
      membershipUpsertJson: '{\n  "items": [\n    {\n      "productCode": "ABC123",\n      "priceListId": "<uuid-list>",\n      "enabled": true\n    }\n  ]\n}',

      providerLinks: [],
      linksLoading: false,
      linksUpsertLoading: false,
      linkFilters: { providerId: "", productCode: "" },
      linksUpsertJson: '{\n  "items": [\n    {\n      "productCode": "ABC123",\n      "providerId": "<uuid-provider>",\n      "supplierProductCode": "ABC123-EXT",\n      "costPrice": 100\n    }\n  ]\n}',

      stockQueryForm: { productCodesCsv: "", warehouseCodesCsv: "", includeZero: true },
      stockQueryLoading: false,
      stockRows: [],
      stockAdjustJson: '{\n  "adjustments": [\n    {\n      "productCode": "ABC123",\n      "warehouseCode": "01",\n      "quantityDelta": 10,\n      "availableDelta": 10,\n      "movementType": "bulk_adjust"\n    }\n  ]\n}',
      stockAdjustLoading: false,
      stockAdjustResult: "",

      movementFilters: { productCode: "", warehouseCode: "", movementType: "", dateFrom: "", dateTo: "" },
      movementsLoading: false,
      stockMovements: [],

      auditFilters: { resourceType: "", action: "", success: "all", dateFrom: "", dateTo: "" },
      auditLoading: false,
      auditLogs: [],
      mutationConfirmDialog: false,
      mutationConfirmAction: "",
      mutationConfirmLabel: "",
      mutationConfirmLoading: false,
      mutationSuccessDialog: false,
      mutationSuccessMessage: "",

      previewHeaders: [{ text: "Código", value: "productCode" }, { text: "Producto", value: "productName" }, { text: "Precio anterior", value: "previousPrice" }, { text: "Precio nuevo", value: "newPrice" }, { text: "Variación %", value: "changePercent" }],
      providerHeaders: [{ text: "Código", value: "code" }, { text: "Nombre", value: "name" }, { text: "Estado", value: "isActive" }, { text: "Acciones", value: "actions", sortable: false }],
      rubroCatalogHeaders: [
        { text: "Rubro", value: "rubro" },
        { text: "Descripción", value: "description" },
        { text: "Artículos", value: "productsCount" },
        { text: "Proveedores", value: "providersCount" },
      ],
      formulaHeaders: [{ text: "Código", value: "code" }, { text: "Nombre", value: "name" }, { text: "Expresión", value: "expression" }, { text: "Estado", value: "isActive" }, { text: "Acciones", value: "actions", sortable: false }],
      membershipHeaders: [{ text: "Producto", value: "product.code" }, { text: "Lista", value: "priceList.code" }, { text: "Habilitado", value: "enabled" }, { text: "Fuente", value: "sourceType" }],
      linkHeaders: [{ text: "Proveedor", value: "provider.code" }, { text: "Producto", value: "product.code" }, { text: "Código proveedor", value: "supplierProductCode" }, { text: "Costo", value: "costPrice" }, { text: "Activo", value: "isActive" }],
      stockHeaders: [{ text: "Producto", value: "productCode" }, { text: "Depósito", value: "warehouseCode" }, { text: "Cantidad", value: "quantity" }, { text: "Disponible", value: "available" }, { text: "Reservado", value: "reserved" }],
      movementHeaders: [{ text: "Fecha", value: "createdAt" }, { text: "Producto", value: "product.code" }, { text: "Depósito", value: "warehouse.code" }, { text: "Tipo", value: "movementType" }, { text: "Delta", value: "quantityDelta" }],
      auditHeaders: [{ text: "Fecha", value: "createdAt" }, { text: "Acción", value: "action" }, { text: "Recurso", value: "resourceType" }, { text: "ID recurso", value: "resourceId" }, { text: "Estado", value: "success" }],

      importDialog: false,
      importFile: null,
      importListId: "",
      importDescription: "Importación desde Excel",
      importLoading: false,
      importResult: null,
      importError: "",
      importPreviewHeaders: [
        { text: "Fila", value: "row" },
        { text: "Código", value: "code" },
        { text: "Artículo", value: "productName" },
        { text: "Precio actual", value: "currentPrice" },
        { text: "Precio nuevo", value: "newPrice" },
        { text: "Estado", value: "status" },
      ],

      modeOptions: [
        { text: "Multiplicador", value: "multiplier" },
        { text: "Precio absoluto", value: "absolute" },
        { text: "Delta (suma/resta)", value: "delta" },
        { text: "Expresión", value: "expression" },
      ],
      coefficientSourceOptions: [
        { text: "Usar coeficiente del artículo", value: "product" },
        { text: "Coeficiente fijo", value: "fixed" },
      ],
      fieldOptions: ["price", "priceSale1", "priceSale2", "priceSale3", "utilityPercent"],
      operationOptions: ["set", "increment", "multiply"],
      sortOptions: ["code", "name", "price"],
      sortDirOptions: ["asc", "desc"],
      customerTypeOptions: [
        { text: "Todos", value: "all" },
        { text: "Consumidor final", value: "final_consumer" },
        { text: "Cuenta corriente", value: "account_current" },
      ],
      activeOptions: [{ text: "Todos", value: "all" }, { text: "Activos", value: true }, { text: "Inactivos", value: false }],
      booleanAnyOptions: [{ text: "Todos", value: "all" }, { text: "true", value: true }, { text: "false", value: false }],
      productActiveOptions: [{ text: "Todos", value: "all" }, { text: "Activos", value: true }, { text: "Inactivos", value: false }],
      productStatusOptions: [
        { text: "Todos", value: "all" },
        { text: "0 - Baja (solo venta)", value: 0 },
        { text: "1 - Activo", value: 1 },
        { text: "2 - Sin reposición", value: 2 },
        { text: "3 - Baja (no vender)", value: 3 },
      ],
    };
  },
  computed: {
    roles() {
      return (this.$store.getters["auth/user"] && this.$store.getters["auth/user"].roles) || [];
    },
    userName() {
      const user = this.$store.state.auth.user;
      return user ? (user.username || user.email || "Usuario") : "Usuario";
    },
    isAdmin() {
      return Array.isArray(this.roles) && this.roles.includes("admin");
    },
    canImportPreview() {
      return Boolean(this.importListId && this.importFile);
    },
    canImportApply() {
      return Boolean(this.canImportPreview && this.importResult && this.importResult.preview && this.importResult.fileHash);
    },
    normalizedRoles() {
      return (Array.isArray(this.roles) ? this.roles : [])
        .map((x) => String(x || "").trim().toLowerCase());
    },
    isCounterOperator() {
      return this.normalizedRoles.includes("operario_mostrador")
        || this.normalizedRoles.includes("operario mostrador")
        || this.normalizedRoles.includes("mostrador");
    },
    cartTotal() {
      const base = this.cart.reduce((sum, item) => sum + item.finalPrice * item.quantity, 0);
      const disc = Number(this.cartGlobalDiscount || 0);
      const total = base * (1 - disc / 100);
      console.log("[REALTIME CART TOTAL DIAGNOSTIC]", {
        cartLength: this.cart.length,
        baseSum: base,
        cartGlobalDiscount: this.cartGlobalDiscount,
        parsedDisc: disc,
        finalTotal: total
      });
      return total;
    },
    pageTitle() {
      if (this.viewMode === "sales") return "Ventas - Lista de Precios";
      if (this.viewMode === "pricing") return "Administración de Precios";
      return "Precios y Stock";
    },
    pageSubtitle() {
      if (this.viewMode === "sales") return "Consulta comercial para mostrador con catálogo y precio final.";
      if (this.viewMode === "pricing") return "Gestión de aumentos, proveedores, fórmulas y relaciones comerciales.";
      return this.isAdmin
        ? "Operación completa de precios, stock, proveedores, fórmulas y auditoría."
        : "Consulta comercial para mostrador con catálogo, filtros y precio final.";
    },
    canShowCatalogTab() {
      return this.viewMode !== "pricing";
    },
    canShowEffectiveTab() {
      if (this.isCounterOperator) return false;
      return this.viewMode !== "pricing";
    },
    canShowUpdatesTab() {
      return this.isAdmin && this.viewMode !== "sales";
    },
    canShowProvidersTab() {
      return this.isAdmin && this.viewMode !== "sales";
    },
    canShowMembershipsTab() {
      // Oculto en UI para simplificar la experiencia de negocio.
      return false;
    },
    canShowStockTab() {
      return this.isAdmin && this.viewMode === "full";
    },
    canExportCatalog() {
      return this.isAdmin;
    },
    currentListMatrixDiscounts() {
      const list = this.priceLists.find((x) => x.id === this.catalogFilters.priceListId);
      if (!list) return [];
      
      const discountsSet = new Set();
      
      if (list.attributes) {
        String(list.attributes)
          .split(",")
          .map((s) => parseFloat(s.trim()))
          .filter((n) => !isNaN(n) && n !== 0)
          .forEach((val) => discountsSet.add(Math.abs(val)));
      }
      
      if (list.calculationExpression) {
        try {
          const match = String(list.calculationExpression).match(/price\s*\*\s*([0-9.]+)/i);
          if (match && match[1]) {
            const factor = parseFloat(match[1]);
            if (!isNaN(factor) && factor < 1 && factor > 0) {
              const autoDisc = Number((Math.abs(1 - factor) * 100).toFixed(2));
              if (autoDisc > 0) {
                discountsSet.add(autoDisc);
              }
            }
          }
        } catch (e) {}
      }
      
      const finalDiscounts = Array.from(discountsSet).sort((a, b) => a - b);
      console.log("[REALTIME MATRIX DIAGNOSTIC] Discounts detected:", finalDiscounts, "for list:", list.code);
      return finalDiscounts;
    },
    catalogHeadersView() {
      if (this.isAdmin && this.catalogShowAdvancedCols) return this.catalogHeaders;
      
      const baseHeaders = [
        { text: "Img", value: "imagePath", sortable: false, align: "center" },
        { text: "Código", value: "code", align: "center" },
        { text: "Descripción", value: "name", align: "left" },
        { text: "Stock Disp.", value: "stockAvailable", align: "center" },
        { text: "Lista", value: "formattedFinalPrice", align: "center" }
      ];

      const discounts = this.currentListMatrixDiscounts;
      discounts.forEach((disc) => {
        baseHeaders.push({
          text: `Lista -${disc}%`,
          value: `price_matrix_${disc}`,
          sortable: false,
          align: "center"
        });
      });

      baseHeaders.push(
        { text: "Último cambio", value: "lastPriceChangeAt", align: "center" },
        { text: "", value: "cartAction", sortable: false, align: "center" }
      );
      
      console.log("[REALTIME MATRIX DIAGNOSTIC] catalogHeadersView output:", baseHeaders.map(h => h.text));
      return baseHeaders;
    },
    canShowAuditTab() {
      return this.isAdmin && this.viewMode === "full";
    },
    priceListOptions() {
      return this.priceLists
        .filter((x) => Boolean(x.isActive))
        .map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    providerOptions() {
      return this.providers.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    formulaOptions() {
      return this.formulas.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    formulaPercentExpression() {
      const pct = Number(this.formulaPercentForm.percent || 0);
      const multiplier = this.formulaPercentForm.operation === "decrease"
        ? 1 - pct / 100
        : 1 + pct / 100;
      return `PRICE * (${multiplier})`;
    },
    formulaPercentPreview() {
      const pct = Number(this.formulaPercentForm.percent || 0);
      if (this.formulaPercentForm.operation === "decrease") return `Decrementar ${pct}%`;
      return `Incrementar ${pct}%`;
    },
    currentListRuleLabel() {
      const list = this.priceLists.find((x) => x.id === this.listRuleForm.priceListId);
      if (!list) return "Sin regla vinculada";
      if (list.priceFormula && list.priceFormula.expression) {
        return `${list.priceFormula.code} - ${this.describeExpression(list.priceFormula.expression)}`;
      }
      if (list.calculationExpression) return this.describeExpression(list.calculationExpression);
      return "Sin regla vinculada";
    },
    activeListLabel() {
      const list = this.priceLists.find((x) => x.id === this.catalogFilters.priceListId);
      if (!list) return "Sin seleccionar";
      return `${list.code} - ${list.name}`;
    },
    byProviderScopeLabel() {
      const parts = [];
      const rubro = this.normalizeRubroFilter(this.byProviderForm.rubro);
      const codes = splitCsv(this.byProviderForm.productCodesCsv);
      if (rubro) parts.push(`rubro ${rubro}`);
      if (this.byProviderForm.codeFrom || this.byProviderForm.codeTo) {
        parts.push(`rango de códigos ${this.byProviderForm.codeFrom || "inicio"} - ${this.byProviderForm.codeTo || "fin"}`);
      }
      if (codes.length) parts.push(`${codes.length} código(s) exacto(s)`);
      if (!parts.length) return "Modificando precio de todos los artículos del proveedor seleccionado.";
      return `Modificando precio del proveedor con filtros: ${parts.join(", ")}.`;
    },
  },
  created() {
    this.setTabFromRoute();
    this.bootstrap();
  },
  mounted() {
    this.initRealtimeEvents();
  },
  watch: {
    "$route.query.tab"() {
      this.setTabFromRoute();
    },
    customerSearchQuery(val) {
      if (this.customerSearchDebounce) clearTimeout(this.customerSearchDebounce);
      this.customerSearchDebounce = setTimeout(() => {
        this.searchCustomers(val);
      }, 400);
    },
  },
  beforeDestroy() {
    if (this.catalogSearchDebounce) clearTimeout(this.catalogSearchDebounce);
    if (this.customerSearchDebounce) clearTimeout(this.customerSearchDebounce);
    if (this.sseSource) {
      try { this.sseSource.close(); } catch (e) {}
      this.sseSource = null;
    }
  },
  methods: {
    initRealtimeEvents() {
      try {
        const baseUrl = (process.env.VUE_APP_API_URL || "").replace(/\/+$/, "");
        this.sseSource = new EventSource(`${baseUrl}/realtime/stream`);
        this.sseSource.onmessage = (e) => {
          try {
            const payload = JSON.parse(e.data);
            if (payload && payload.event === "priceListsUpdated") {
              this.handleRealtimePriceListUpdate(payload);
            } else if (payload && payload.event === "stockUpdated") {
              if (this.canShowCatalogTab && this.tab === this.visibleTabKeys().indexOf("catalog")) {
                this.fetchCatalog();
              }
            }
          } catch (err) {}
        };
      } catch (error) {
        console.error("Realtime sync init failed:", error);
      }
    },
    async handleRealtimePriceListUpdate(payload) {
      try {
        const response = await priceListsService.list();
        const lists = this.unwrap(response) || [];
        if (lists.length) {
          this.priceLists = lists;
          if (this.catalogFilters.priceListId === payload.listId) {
            this.applySelectedList(this.catalogFilters.priceListId);
            await this.fetchCatalog();
          }
        }
      } catch (e) {}
    },
    visibleTabKeys() {
      const keys = [];
      if (this.canShowCatalogTab) keys.push("catalog");
      if (this.canShowEffectiveTab) keys.push("effective");
      if (this.canShowUpdatesTab) keys.push("updates");
      if (this.canShowProvidersTab) keys.push("providers");
      if (this.canShowMembershipsTab) keys.push("memberships");
      if (this.canShowStockTab) keys.push("stock");
      if (this.canShowAuditTab) keys.push("audit");
      return keys;
    },
    setTabFromRoute() {
      const wanted = String((this.$route && this.$route.query && this.$route.query.tab) || "").trim();
      if (!wanted) {
        this.tab = 0;
        return;
      }
      const keys = this.visibleTabKeys();
      const idx = keys.indexOf(wanted);
      this.tab = idx >= 0 ? idx : 0;
    },
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    getError(error) {
      if (error && error.message === "Network Error") {
        return "No hay conexión con el servidor (puerto 8129). Verifica que el backend esté levantado.";
      }
      if (error && error.response && error.response.data) {
        if (error.response.data.message) return error.response.data.message;
        if (error.response.data.errors) return String(error.response.data.errors);
      }
      if (error && error.message) return error.message;
      return "No pudimos completar la operación.";
    },
    scrollPreviewIntoView() {
      this.$nextTick(() => {
        const table = this.$refs.previewTable;
        if (table && typeof table.$el?.scrollIntoView === "function") {
          table.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    clearMessages() {
      this.error = "";
      this.info = "";
      this.success = "";
    },
    onError(error) {
      this.success = "";
      const errorMsg = this.getError(error);
      this.error = errorMsg;
      this.activeErrorMsg = errorMsg;
      this.errorDialog = true;
    },
    getMutationConfirmMeta(action) {
      const map = {
        applyByProvider: { label: "Aplicar actualización por proveedor." },
        applyBulk: { label: "Aplicar actualización masiva." },
        applyByField: { label: "Aplicar actualización por campo." },
        saveProvider: { label: "Guardar proveedor." },
        linkRuleToList: { label: "Vincular regla a lista." },
        unlinkRuleFromList: { label: "Quitar regla de lista." },
        saveFormula: { label: "Guardar fórmula." },
        runStockAdjust: { label: "Aplicar ajustes de stock." },
      };
      return map[action] || null;
    },
    requestMutationConfirm(action) {
      const meta = this.getMutationConfirmMeta(action);
      if (!meta || typeof this[action] !== "function") return;
      this.mutationConfirmAction = action;
      this.mutationConfirmLabel = meta.label;
      this.mutationConfirmDialog = true;
    },
    isPriceMutationAction(action) {
      return action === "applyByProvider" || action === "applyBulk" || action === "applyByField";
    },
    clearPriceMutationScreen(action) {
      const activeListId = this.catalogFilters.priceListId || this.byProviderForm.priceListId || this.bulkForm.priceListId || this.byFieldForm.priceListId || "";
      this.previewRows = [];
      this.bulkSummary = "";
      this.byProviderSummary = "";
      this.byFieldResult = "";
      if (action === "applyByProvider") {
        this.byProviderForm = {
          priceListId: activeListId,
          providerId: "",
          updateMethod: "percent",
          changeType: "increase",
          percent: null,
          coefficientSource: "product",
          fixedCoefficient: null,
          rubro: "",
          codeFrom: "",
          codeTo: "",
          productCodesCsv: "",
          suggestedProductCode: "",
          searchText: "",
        };
        this.byProviderSelectedSuggestion = null;
        this.byProviderSuggestions = [];
        this.byProviderRubroOptions = [];
      }
      if (action === "applyBulk") {
        this.bulkForm = {
          priceListId: activeListId,
          mode: "multiplier",
          value: null,
          formulaId: "",
          expression: "",
          providerId: "",
          rubro: "",
          subrubro: "",
          isProductActive: "all",
          productCodesCsv: "",
          affectDependencies: false,
        };
      }
      if (action === "applyByField") {
        this.byFieldForm = {
          priceListId: activeListId,
          targetField: "price",
          operation: "set",
          value: null,
          productIdsCsv: "",
          productCodesCsv: "",
        };
      }
    },
    async confirmMutation() {
      const action = this.mutationConfirmAction;
      if (!action || typeof this[action] !== "function") return;
      this.mutationConfirmLoading = true;
      this.mutationSuccessMessage = "";
      try {
        await this[action]();
        if (!this.error) {
          const okMessage = this.success || "La operación se ejecutó correctamente.";
          if (this.isPriceMutationAction(action)) {
            this.clearPriceMutationScreen(action);
          }
          this.success = "";
          this.mutationConfirmDialog = false;
          this.mutationSuccessMessage = okMessage;
          this.mutationSuccessDialog = true;
        } else {
          this.mutationConfirmDialog = false;
        }
      } finally {
        this.mutationConfirmLoading = false;
      }
    },

    openImportDialog() {
      this.importDialog = true;
      this.importFile = null;
      this.importResult = null;
      this.importError = "";
      if (this.priceLists.length) {
        const principal = this.priceLists.find((x) => x.code === "WEB202607");
        this.importListId = principal ? principal.id : this.priceLists[0].id;
      }
    },
    closeImportDialog() {
      this.importDialog = false;
      this.importFile = null;
      this.importResult = null;
      this.importError = "";
    },
    async runImportUpload(preview) {
      if (!this.canImportPreview) return;
      this.importLoading = true;
      this.importError = "";
      try {
        const formData = new FormData();
        formData.append("file", this.importFile);
        formData.append("description", this.importDescription || "Importación desde gestión de precios");
        if (!preview) formData.append("previewHash", this.importResult.fileHash);
        const response = await priceListsService.uploadItemsFile(
          this.importListId,
          formData,
          { preview, skipEmptyPrices: true }
        );
        const data = response && response.data ? (response.data.data || response.data) : null;
        this.importResult = { ...(data || {}), applied: !preview };
        if (!preview) {
          this.info = `Importación aplicada: ${this.importResult.processed} artículos procesados, ${this.importResult.updated} actualizados, ${this.importResult.created} creados, ${this.importResult.skipped} omitidos.`;
          await this.bootstrap();
        }
      } catch (error) {
        const responseData = error && error.response && error.response.data;
        const msg = responseData && (responseData.message || responseData.errors || responseData.error)
          ? String(responseData.message || responseData.errors || responseData.error)
          : String(error.message || error);
        this.importError = msg;
      } finally {
        this.importLoading = false;
      }
    },

    async bootstrap() {
      this.bootstrapLoading = true;
      this.clearMessages();
      try {
        const tasks = [this.fetchPriceLists(), this.fetchProviders(), this.fetchFormulas()];
        if (this.isAdmin) tasks.push(this.fetchRubroCatalog());
        await Promise.all(tasks);
        this.syncListRuleFromList();
        if (this.canShowCatalogTab) await this.fetchCatalog();
      } catch (error) {
        this.onError(error);
      } finally {
        this.bootstrapLoading = false;
      }
    },

    async fetchPriceLists() {
      const response = await priceListsService.list();
      this.priceLists = this.unwrap(response) || [];
      if (!this.priceLists.length) return;
      const savedListId = localStorage.getItem("herra.pricing.selectedListId");
      let selected = this.priceLists.find((x) => x.id === savedListId) || null;

      if (!selected) {
        selected = await this.findFirstListWithData();
      }

      if (!selected) {
        selected = this.priceLists[0];
      }

      this.applySelectedList(selected.id);
    },

    applySelectedList(listId) {
      this.catalogFilters.priceListId = listId;
      this.bulkForm.priceListId = listId;
      this.byProviderForm.priceListId = listId;
      this.byFieldForm.priceListId = listId;
      this.effectiveForm.priceListId = listId;
      this.listRuleForm.priceListId = listId;
      this.cartGlobalDiscount = 0;
      localStorage.setItem("herra.pricing.selectedListId", listId);
      if (this.byProviderForm.providerId) this.fetchByProviderRubros();
    },

    async findFirstListWithData() {
      for (let i = 0; i < this.priceLists.length; i += 1) {
        const list = this.priceLists[i];
        try {
          const response = await pricingStockService.getSalesCatalog({
            priceListId: list.id,
            includeZero: true,
            sortBy: "code",
            sortDir: "asc",
            limit: 1,
            quantity: 1,
            customerType: "all",
          });
          const data = this.unwrap(response) || {};
          if (Number(data.returned || 0) > 0) return list;
        } catch (_error) {
          // ignore and continue searching
        }
      }
      return null;
    },

    async fetchCatalogSuggestions(search, priceListId, extraParams = {}) {
      const response = await pricingStockService.getSalesCatalogSuggest({ q: search, priceListId, ...extraParams });
      const rows = this.unwrap(response) || [];
      return rows.map((x) => ({ ...x, label: `${x.code} - ${x.name}` }));
    },
    async onCatalogSuggest(search) {
      if (!search || search.length < 2) {
        this.catalogSuggestions = [];
        return;
      }
      this.catalogSuggestLoading = true;
      try {
        this.catalogSuggestions = await this.fetchCatalogSuggestions(search, this.catalogFilters.priceListId);
      } catch (_error) {
        this.catalogSuggestions = [];
      } finally {
        this.catalogSuggestLoading = false;
      }
    },
    onCatalogSearchInput(search) {
      this.catalogFilters.searchText = search || "";
      this.catalogFilters.exactSelected = false;
      if (this.catalogFilters.suggestedProductCode && search !== this.catalogFilters.suggestedProductCode) {
        this.catalogFilters.suggestedProductCode = "";
      }
      this.onCatalogSuggest(search || "");
      if (!this.isAdmin) this.onSellerSearchInput();
    },
    onCatalogOptionSelected(value) {
      if (!value) return;
      this.catalogFilters.suggestedProductCode = value;
      this.catalogFilters.searchText = value;
      this.catalogFilters.exactSelected = true;
      this.fetchCatalog();
    },
    async onByProviderSearchInput(search) {
      this.byProviderForm.searchText = search || "";
      const selectedCode = this.byProviderSelectedSuggestion && this.byProviderSelectedSuggestion.code
        ? String(this.byProviderSelectedSuggestion.code)
        : "";
      if (selectedCode && search !== selectedCode) {
        this.byProviderSelectedSuggestion = null;
      }
      if (!search || search.length < 2) {
        this.byProviderSuggestions = [];
        return;
      }
      this.byProviderSuggestLoading = true;
      try {
        const params = {};
        if (this.byProviderForm.providerId) params.providerId = this.byProviderForm.providerId;
        const rubro = this.normalizeRubroFilter(this.byProviderForm.rubro);
        if (rubro) params.rubro = rubro;
        if (this.byProviderForm.codeFrom) params.codeFrom = this.byProviderForm.codeFrom;
        if (this.byProviderForm.codeTo) params.codeTo = this.byProviderForm.codeTo;
        this.byProviderSuggestions = await this.fetchCatalogSuggestions(search, this.byProviderForm.priceListId, params);
      } catch (_error) {
        this.byProviderSuggestions = [];
      } finally {
        this.byProviderSuggestLoading = false;
      }
    },
    async onByProviderOptionSelected(value) {
      if (!value) return;
      const selected = typeof value === "string"
        ? this.byProviderSuggestions.find((x) => x.code === value) || { code: value }
        : value;
      const selectedCode = String(selected.code || "").trim();
      if (!selectedCode) return;

      if (!this.byProviderForm.priceListId) {
        this.byProviderForm.priceListId = this.catalogFilters.priceListId || (this.priceListOptions[0] && this.priceListOptions[0].id) || "";
      }
      if (!this.byProviderForm.providerId && selected.providerId) {
        this.byProviderForm.providerId = selected.providerId;
        await this.fetchByProviderRubros();
      }
      if (!this.byProviderForm.providerId) {
        await this.tryAutofillProviderByProductCode(selectedCode);
      }

      const currentCodes = splitCsv(this.byProviderForm.productCodesCsv);
      if (!currentCodes.includes(selectedCode)) currentCodes.push(selectedCode);
      this.byProviderForm.productCodesCsv = currentCodes.join(", ");
      this.byProviderForm.searchText = selectedCode;
      this.byProviderSelectedSuggestion = selected;
    },
    async tryAutofillProviderByProductCode(productCode) {
      try {
        const response = await pricingStockService.getProviderLinks({ productCode, isActive: true });
        const rows = this.unwrap(response) || [];
        if (!rows.length) return;
        const primary = rows.find((x) => Boolean(x.isPrimary)) || rows[0];
        if (!primary || !primary.providerId) return;
        this.byProviderForm.providerId = primary.providerId;
        await this.fetchByProviderRubros();
      } catch (_error) {
        // Silent fallback: if no link found, user can still choose provider manually.
      }
    },
    normalizeRubroFilter(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value.trim();
      if (typeof value === "object") {
        if (value.value !== undefined && value.value !== null) return String(value.value).trim();
        if (value.label !== undefined && value.label !== null) {
          const fromLabel = String(value.label).split(" ")[0];
          return fromLabel.trim();
        }
      }
      return String(value || "").trim();
    },
    onByProviderProviderChanged() {
      this.byProviderForm.searchText = "";
      this.byProviderSelectedSuggestion = null;
      this.byProviderSuggestions = [];
      this.fetchByProviderRubros();
    },
    onByProviderListChanged() {
      this.byProviderForm.searchText = "";
      this.byProviderSelectedSuggestion = null;
      this.byProviderSuggestions = [];
      this.fetchByProviderRubros();
    },
    async fetchByProviderRubros() {
      if (!this.byProviderForm.providerId) {
        this.byProviderRubroOptions = [];
        return;
      }
      this.byProviderRubroLoading = true;
      try {
        const params = {};
        if (this.byProviderForm.priceListId) params.priceListId = this.byProviderForm.priceListId;
        const response = await pricingStockService.getProviderRubros(this.byProviderForm.providerId, params);
        const rows = this.unwrap(response) || [];
        this.byProviderRubroOptions = rows.map((x) => ({
          value: x.value,
          label: x.description ? `${x.value} - ${x.description} (${x.count})` : `${x.value} (${x.count})`,
        }));
      } catch (_error) {
        this.byProviderRubroOptions = [];
      } finally {
        this.byProviderRubroLoading = false;
      }
    },

    async fetchCatalog() {
      this.catalogLoading = true;
      this.clearMessages();
      try {
        if (!this.catalogFilters.priceListId) {
          this.info = "Selecciona una lista de precios.";
          this.catalogRows = [];
          return;
        }

        this.applySelectedList(this.catalogFilters.priceListId);

        const qText = this.catalogFilters.suggestedProductCode || this.catalogFilters.searchText || undefined;
        const params = {
          priceListId: this.catalogFilters.priceListId,
          customerType: "all",
          quantity: 1,
          q: qText,
          sortBy: "code",
          sortDir: "asc",
          includeZero: this.isAdmin ? this.catalogFilters.includeZero : true,
          exactCode: Boolean(this.catalogFilters.exactSelected && this.catalogFilters.suggestedProductCode),
          limit: this.catalogFilters.exactSelected ? 60 : (this.isAdmin ? 200 : 600),
        };
        if (this.isAdmin && this.catalogFilters.providerId) params.providerId = this.catalogFilters.providerId;
        if (this.isAdmin && this.catalogFilters.productStatus !== "all") params.productStatus = this.catalogFilters.productStatus;
        if (this.isAdmin && this.catalogFilters.codeFrom) params.codeFrom = this.catalogFilters.codeFrom;
        if (this.isAdmin && this.catalogFilters.codeTo) params.codeTo = this.catalogFilters.codeTo;

        const response = await pricingStockService.getSalesCatalog(params);
        const data = this.unwrap(response) || {};
        const rawRows = data.rows || [];
        this.catalogRows = rawRows.map((item) => {
          const newItem = { ...item };
          const p = Number(item.price || 0);
          const fp = Number(item.finalPrice || 0);
          newItem.formattedPrice = `$ ${this.formatCurrency(p)}`;
          newItem.formattedFinalPrice = `$ ${this.formatCurrency(fp)}`;
          this.currentListMatrixDiscounts.forEach((disc) => {
            const val = fp * (1 - disc / 100);
            newItem[`price_matrix_${disc}`] = `$ ${this.formatCurrency(val)}`;
          });
          return newItem;
        });
        if (!this.catalogRows.length) {
          this.info = `La lista ${this.activeListLabel} no tiene resultados con los filtros actuales.`;
        }
      } catch (error) {
        this.onError(error);
      } finally {
        this.catalogLoading = false;
      }
    },
    async exportCatalogPriceList() {
      this.catalogExportLoading = true;
      this.clearMessages();
      try {
        if (!this.canExportCatalog) {
          throw new Error("No autorizado para descargar XLSX.");
        }
        if (!this.catalogFilters.priceListId) {
          throw new Error("Selecciona una lista de precios.");
        }
        await this.exportCatalogPriceListFallback(this.catalogFilters.priceListId);
        this.success = "Lista de precios descargada.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.catalogExportLoading = false;
      }
    },
    buildCatalogParams(limitOverride) {
      const qText = this.catalogFilters.suggestedProductCode || this.catalogFilters.searchText || undefined;
      const params = {
        priceListId: this.catalogFilters.priceListId,
        customerType: "all",
        quantity: 1,
        q: qText,
        sortBy: "code",
        sortDir: "asc",
        includeZero: this.isAdmin ? this.catalogFilters.includeZero : true,
        exactCode: Boolean(this.catalogFilters.exactSelected && this.catalogFilters.suggestedProductCode),
        limit: limitOverride || (this.catalogFilters.exactSelected ? 60 : (this.isAdmin ? 200 : 600)),
      };
      if (this.isAdmin && this.catalogFilters.providerId) params.providerId = this.catalogFilters.providerId;
      if (this.isAdmin && this.catalogFilters.productStatus !== "all") params.productStatus = this.catalogFilters.productStatus;
      if (this.isAdmin && this.catalogFilters.codeFrom) params.codeFrom = this.catalogFilters.codeFrom;
      if (this.isAdmin && this.catalogFilters.codeTo) params.codeTo = this.catalogFilters.codeTo;
      return params;
    },
    async downloadImportTemplate() {
      this.importTemplateLoading = true;
      this.importError = "";
      try {
        if (!this.importListId) throw new Error("Seleccioná la lista destino para descargar su plantilla.");
        // La plantilla de importación no debe arrastrar ni sobrescribir toda
        // la lista: sólo contiene las dos columnas que el importador requiere.
        const worksheet = XLSX.utils.aoa_to_sheet([["Código", "Precio"]]);
        worksheet["!cols"] = [{ wch: 20 }, { wch: 16 }];
        worksheet["!autofilter"] = { ref: "A1:B1" };
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Precios");
        const selectedList = this.priceLists.find((list) => list.id === this.importListId);
        const listCode = selectedList ? selectedList.code : "lista";
        const fileArray = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([fileArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `plantilla-importar-precios-${String(listCode).toLowerCase()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.importError = error && error.message ? error.message : "No se pudo descargar la plantilla.";
      } finally {
        this.importTemplateLoading = false;
      }
    },
    async exportCatalogPriceListFallback(priceListId = this.catalogFilters.priceListId) {
      const response = await pricingStockService.getSalesCatalogExport({
        ...this.buildCatalogParams(20000),
        priceListId,
        offset: 0,
      });
      const data = this.unwrap(response) || {};
      const rows = Array.isArray(data.rows) ? data.rows : [];
      const expected = Number(data.total || 0);
      if (rows.length < expected) {
        throw new Error(`La exportación quedó incompleta (${rows.length} de ${expected} artículos).`);
      }
      const discounts = this.currentListMatrixDiscounts;
      const N = discounts.length;

      const tableHeaders = ["CODIGO", "DESCRIPCION", "OBS", "PRECIO"];
      discounts.forEach((d) => tableHeaders.push(`-${d}%`));
      tableHeaders.push("UV", "ENV", "Imagen", "ULT.FECHA", "%", "P ANT");

      const tableRows = rows.map((item) => {
        const currentPrice = Number(item.price || item.finalPrice || 0);
        const lastRegisteredPrice = this.getLastRegisteredPrice(item);
        const rowData = [
          item.code || "",
          item.name || "",
          this.getItemObservationsFromMaster(item),
          currentPrice
        ];
        discounts.forEach((d) => {
          rowData.push(Number((currentPrice * (1 - d / 100)).toFixed(2)));
        });
        rowData.push(
          item.saleUnit || "",
          item.packageUnit || "",
          item.imagePath ? this.resolveImageUrl(item.imagePath) : "",
          this.formatDate(item.lastPriceChangeAt),
          this.getExportPriceChangePercentText(item, currentPrice, lastRegisteredPrice),
          Number.isFinite(Number(lastRegisteredPrice)) ? Number(lastRegisteredPrice) : ""
        );
        return rowData;
      });

      const headerRows = this.buildPriceListExcelHeaderRows(this.formatDate(new Date()));
      const aoa = [...headerRows, tableHeaders, ...tableRows];
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);

      const baseCols = [
        { wch: 12 },
        { wch: 34 },
        { wch: 14 },
        { wch: 12 }
      ];
      discounts.forEach(() => baseCols.push({ wch: 12 }));
      baseCols.push(
        { wch: 6 },
        { wch: 7 },
        { wch: 45 },
        { wch: 12 },
        { wch: 8 },
        { wch: 12 }
      );
      worksheet["!cols"] = baseCols;

      const lastColIdx = 9 + N;
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
        { s: { r: 0, c: 2 }, e: { r: 0, c: 6 } },
        { s: { r: 0, c: 7 }, e: { r: 0, c: lastColIdx } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: lastColIdx } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: lastColIdx } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: lastColIdx } },
        { s: { r: 4, c: 0 }, e: { r: 4, c: lastColIdx } },
        { s: { r: 5, c: 0 }, e: { r: 5, c: lastColIdx } },
      ];
      this.applyPriceListWorksheetStyles(worksheet, headerRows.length + 1, tableRows.length, tableHeaders.length);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Lista");
      const fileArray = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([fileArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const selectedList = this.priceLists.find((x) => x.id === priceListId);
      const listCode = selectedList ? selectedList.code : "lista";
      const filename = `lista-precios-${String(listCode).toLowerCase()}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    applyPriceListWorksheetStyles(ws, tableHeaderRow, dataCount, dynamicColCount) {
      const maxCol = dynamicColCount - 1;
      const topFill = { fgColor: { rgb: "C9EEF0" } };
      const noteFill = { fgColor: { rgb: "F1EDBE" } };
      const headerFill = { fgColor: { rgb: "C9EEF0" } };
      const headerFont = { bold: true, color: { rgb: "000000" } };
      const normalFont = { color: { rgb: "000000" } };
      const center = { horizontal: "center", vertical: "center" };
      const left = { horizontal: "left", vertical: "center" };
      const border = {
        top: { style: "thin", color: { rgb: "8EA0A8" } },
        bottom: { style: "thin", color: { rgb: "8EA0A8" } },
        left: { style: "thin", color: { rgb: "8EA0A8" } },
        right: { style: "thin", color: { rgb: "8EA0A8" } },
      };
      const apply = (addr, style) => {
        if (!ws[addr]) return;
        ws[addr].s = style;
      };

      for (let c = 0; c <= maxCol; c += 1) {
        const addr = `${XLSX.utils.encode_col(c)}1`;
        apply(addr, { fill: topFill, font: { ...headerFont, sz: 13 }, alignment: left });
      }
      for (let c = 0; c <= maxCol; c += 1) {
        apply(`${XLSX.utils.encode_col(c)}2`, { font: headerFont, alignment: left });
        apply(`${XLSX.utils.encode_col(c)}3`, { font: headerFont, alignment: left });
        apply(`${XLSX.utils.encode_col(c)}4`, { font: headerFont, alignment: left });
        apply(`${XLSX.utils.encode_col(c)}5`, { fill: noteFill, font: headerFont, alignment: left });
        apply(`${XLSX.utils.encode_col(c)}6`, { fill: noteFill, font: headerFont, alignment: left });
      }

      for (let c = 0; c <= maxCol; c += 1) {
        const addr = `${XLSX.utils.encode_col(c)}${tableHeaderRow}`;
        apply(addr, { fill: headerFill, font: headerFont, alignment: center, border });
      }

      const start = tableHeaderRow + 1;
      const end = start + Math.max(dataCount - 1, 0);
      for (let r = start; r <= end; r += 1) {
        for (let c = 0; c <= maxCol; c += 1) {
          const addr = `${XLSX.utils.encode_col(c)}${r}`;
          const style = { font: normalFont, alignment: left, border };
          
          if (c === 0 || c === 3 || c === maxCol) style.font = { ...normalFont, bold: true };
          
          const isNumber = c === 3 || c === maxCol || (c >= 4 && c < 4 + (dynamicColCount - 10));
          if (isNumber) {
            style.numFmt = "#,##0.00";
            style.alignment = center;
          }
          
          if (c === maxCol - 2 || c === maxCol - 1) {
            style.alignment = center;
          }
          
          apply(addr, style);
        }
      }
    },
    onSellerSearchInput() {
      if (this.isAdmin) return;
      if (this.catalogSearchDebounce) clearTimeout(this.catalogSearchDebounce);
      this.catalogSearchDebounce = setTimeout(() => {
        this.fetchCatalog();
      }, 280);
    },

    async fetchProviders() {
      this.providersLoading = true;
      try {
        const params = {};
        if (this.providerFilters.q) params.q = this.providerFilters.q;
        if (this.providerFilters.isActive !== "all") params.isActive = this.providerFilters.isActive;
        const response = await pricingStockService.getProviders(params);
        this.providers = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.providersLoading = false;
      }
    },
    async fetchRubroCatalog() {
      this.rubroCatalogLoading = true;
      try {
        const params = {};
        if (this.rubroCatalogFilters.q) params.q = this.rubroCatalogFilters.q;
        const response = await pricingStockService.getRubrosCatalog(params);
        this.rubroCatalog = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.rubroCatalogLoading = false;
      }
    },
    async fetchFormulas() {
      this.formulasLoading = true;
      try {
        const params = {};
        if (this.formulaFilters.q) params.q = this.formulaFilters.q;
        if (this.formulaFilters.isActive !== "all") params.isActive = this.formulaFilters.isActive;
        const response = await pricingStockService.getPriceFormulas(params);
        this.formulas = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.formulasLoading = false;
      }
    },
    async fetchBonuses() {
      this.bonusLoading = true;
      try {
        await pricingStockService.getBonuses({ priceListId: this.catalogFilters.priceListId });
        this.success = "Bonificaciones consultadas.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.bonusLoading = false;
      }
    },

    async saveProvider() {
      this.providerSaveLoading = true;
      this.clearMessages();
      try {
        const payload = { code: this.providerForm.code, name: this.providerForm.name, description: this.providerForm.description || null, isActive: this.providerForm.isActive };
        if (this.providerForm.id) await pricingStockService.updateProvider(this.providerForm.id, payload);
        else await pricingStockService.createProvider(payload);
        this.success = "Proveedor guardado correctamente.";
        this.providerForm = { id: "", code: "", name: "", description: "", isActive: true };
        await this.fetchProviders();
      } catch (error) {
        this.onError(error);
      } finally {
        this.providerSaveLoading = false;
      }
    },
    editProvider(item) {
      this.providerForm = { id: item.id, code: item.code, name: item.name, description: item.description || "", isActive: Boolean(item.isActive) };
    },

    async saveFormula() {
      this.formulaSaveLoading = true;
      this.clearMessages();
      try {
        const percent = Number(this.formulaPercentForm.percent);
        if (!Number.isFinite(percent) || percent < 0) throw new Error("Ingresa un porcentaje válido (>= 0).");
        if (this.formulaPercentForm.operation === "decrease" && percent >= 100) {
          throw new Error("El decremento no puede ser 100% o más.");
        }
        const expression = this.formulaPercentExpression;
        const payload = { code: this.formulaForm.code, name: this.formulaForm.name, expression, version: Number(this.formulaForm.version || 1), notes: this.formulaForm.notes || null, isActive: this.formulaForm.isActive };
        if (this.formulaForm.id) await pricingStockService.updatePriceFormula(this.formulaForm.id, payload);
        else await pricingStockService.createPriceFormula(payload);
        this.success = "Regla guardada correctamente.";
        this.resetFormulaEditor();
        this.formulaDialog = false;
        await this.fetchFormulas();
        this.syncListRuleFromList();
      } catch (error) {
        this.onError(error);
      } finally {
        this.formulaSaveLoading = false;
      }
    },
    openCreateFormulaDialog() {
      this.resetFormulaEditor();
      this.formulaDialog = true;
    },
    closeFormulaDialog() {
      this.formulaDialog = false;
      this.resetFormulaEditor();
    },
    resetFormulaEditor() {
      this.formulaForm = { id: "", code: "", name: "", expression: "PRICE", version: 1, notes: "", isActive: true };
      this.formulaPercentForm = { operation: "increase", percent: 10 };
    },
    editFormula(item) {
      this.formulaForm = { id: item.id, code: item.code, name: item.name, expression: item.expression, version: item.version || 1, notes: item.notes || "", isActive: Boolean(item.isActive) };
      const parsed = this.parsePercentRule(item.expression);
      if (parsed) {
        this.formulaPercentForm = parsed;
      } else {
        this.formulaPercentForm = { operation: "increase", percent: 0 };
        this.info = "La regla seleccionada no era porcentual; ajustala con incremento/decremento %.";
      }
      this.formulaDialog = true;
    },
    parsePercentRule(expression) {
      const raw = String(expression || "").trim();
      const match = raw.match(/^PRICE\s*\*\s*\(?\s*([0-9]+(?:\.[0-9]+)?)\s*\)?$/i);
      if (!match) return null;
      const multiplier = Number(match[1]);
      if (!Number.isFinite(multiplier)) return null;
      if (multiplier >= 1) {
        return { operation: "increase", percent: Number(((multiplier - 1) * 100).toFixed(2)) };
      }
      return { operation: "decrease", percent: Number(((1 - multiplier) * 100).toFixed(2)) };
    },
    describeExpression(expression) {
      const raw = String(expression || "").trim();
      if (!raw) return "Sin regla";
      const upper = raw.toUpperCase();
      if (/^PRICE\s*\*\s*\(?\s*([0-9]+(?:\.[0-9]+)?)\s*\)?$/i.test(raw)) {
        const n = Number(RegExp.$1);
        const pct = Number(((n - 1) * 100).toFixed(2));
        if (pct >= 0) return `Precio actual +${pct}%`;
        return `Precio actual ${pct}%`;
      }
      if (/^PRICE\s*\+\s*\(?\s*([+-]?[0-9]+(?:\.[0-9]+)?)\s*\)?$/i.test(raw)) {
        const n = Number(RegExp.$1);
        if (n >= 0) return `Precio actual + ${n}`;
        return `Precio actual - ${Math.abs(n)}`;
      }
      if (/^COST\s*\*\s*\(?\s*([0-9]+(?:\.[0-9]+)?)\s*\)?$/i.test(raw)) {
        const n = Number(RegExp.$1);
        return `Costo x coeficiente ${n}`;
      }
      return upper.replaceAll("PRICE", "PRECIO").replaceAll("COST", "COSTO").replaceAll("COEF", "COEFICIENTE");
    },
    syncListRuleFromList() {
      if (!this.listRuleForm.priceListId) return;
      const list = this.priceLists.find((x) => x.id === this.listRuleForm.priceListId);
      if (!list) return;
      if (list.priceFormulaId) {
        this.listRuleForm.formulaId = list.priceFormulaId;
        return;
      }
      const expr = String(list.calculationExpression || "").trim();
      if (!expr) {
        this.listRuleForm.formulaId = "";
        return;
      }
      const found = this.formulas.find((f) => String(f.expression || "").trim() === expr);
      this.listRuleForm.formulaId = found ? found.id : "";
    },
    async linkRuleToList() {
      this.listRuleSaving = true;
      this.clearMessages();
      try {
        if (!this.listRuleForm.priceListId || !this.listRuleForm.formulaId) throw new Error("Selecciona lista y regla.");
        const formula = this.formulas.find((x) => x.id === this.listRuleForm.formulaId);
        if (!formula) throw new Error("Regla no encontrada.");
        await priceListsService.update(this.listRuleForm.priceListId, { priceFormulaId: formula.id, calculationExpression: formula.expression });
        const list = this.priceLists.find((x) => x.id === this.listRuleForm.priceListId);
        if (list) {
          list.priceFormulaId = formula.id;
          list.priceFormula = formula;
          list.calculationExpression = formula.expression;
        }
        this.success = "Regla vinculada a la lista.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.listRuleSaving = false;
      }
    },
    async unlinkRuleFromList() {
      this.listRuleSaving = true;
      this.clearMessages();
      try {
        if (!this.listRuleForm.priceListId) throw new Error("Selecciona una lista.");
        await priceListsService.update(this.listRuleForm.priceListId, { priceFormulaId: null, calculationExpression: null });
        const list = this.priceLists.find((x) => x.id === this.listRuleForm.priceListId);
        if (list) {
          list.priceFormulaId = null;
          list.priceFormula = null;
          list.calculationExpression = null;
        }
        this.listRuleForm.formulaId = "";
        this.success = "Regla desvinculada de la lista.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.listRuleSaving = false;
      }
    },

    async upsertBonuses() {
      this.bonusUpsertLoading = true;
      this.clearMessages();
      try {
        const payload = parseJsonText(this.bonusUpsertJson);
        if (!payload) throw new Error("JSON vacío en bonificaciones.");
        const response = await pricingStockService.upsertBonuses(payload);
        const data = this.unwrap(response);
        this.success = `Bonificaciones procesadas: ${data.processed}/${data.total}`;
      } catch (error) {
        this.onError(error);
      } finally {
        this.bonusUpsertLoading = false;
      }
    },

    async loadEffectivePrice() {
      this.effectiveLoading = true;
      this.clearMessages();
      try {
        const params = { priceListId: this.effectiveForm.priceListId, productCode: this.effectiveForm.productCode };
        if (this.effectiveForm.providerId) params.providerId = this.effectiveForm.providerId;
        if (this.effectiveForm.atDate) params.atDate = this.effectiveForm.atDate;
        const response = await pricingStockService.getEffectivePrice(params);
        this.effectiveResult = this.unwrap(response);
      } catch (error) {
        this.onError(error);
      } finally {
        this.effectiveLoading = false;
      }
    },

    buildBulkPayload(source) {
      const payload = { priceListId: source.priceListId, mode: source.mode };
      if (source.value !== null && source.value !== "") payload.value = Number(source.value);
      if (source.formulaId) payload.formulaId = source.formulaId;
      if (source.expression) payload.expression = source.expression;
      if (source.providerId) payload.providerId = source.providerId;
      if (source.rubro) payload.rubro = source.rubro;
      if (source.subrubro) payload.subrubro = source.subrubro;
      const codes = splitCsv(source.productCodesCsv);
      if (codes.length) payload.productCodes = codes;
      if (source.isProductActive !== "all") payload.isProductActive = source.isProductActive;
      if (source.affectDependencies !== undefined) payload.affectDependencies = !!source.affectDependencies;
      return payload;
    },

    async previewBulk() {
      this.bulkPreviewLoading = true;
      this.clearMessages();
      this.previewRows = [];
      try {
        const payload = this.buildBulkPayload(this.bulkForm);
        const response = await pricingStockService.previewBulkUpdate(payload);
        const data = this.unwrap(response) || {};
        this.previewRows = data.rows || [];
        this.bulkSummary = `Previsualización generada: ${data.count || 0} filas.`;
        this.scrollPreviewIntoView();
      } catch (error) {
        this.onError(error);
      } finally {
        this.bulkPreviewLoading = false;
      }
    },
    async applyBulk() {
      this.bulkApplyLoading = true;
      this.clearMessages();
      try {
        const payload = this.buildBulkPayload(this.bulkForm);
        payload.description = "Actualización masiva desde frontend";
        const response = await pricingStockService.applyBulkUpdate(payload);
        const data = this.unwrap(response);
        this.bulkSummary = `Aplicado. changed=${data.changed}, dependencies=${data.changedDependencies}, total=${data.totalChanged}.`;
        this.success = "Actualización masiva aplicada.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.bulkApplyLoading = false;
      }
    },
    buildByProviderPayload() {
      if (!this.byProviderForm.priceListId || !this.byProviderForm.providerId) {
        throw new Error("Selecciona lista y proveedor.");
      }

      const payload = {
        priceListId: this.byProviderForm.priceListId,
        providerId: this.byProviderForm.providerId,
      };

      const rubro = this.normalizeRubroFilter(this.byProviderForm.rubro);
      if (rubro) payload.rubro = rubro;
      if (this.byProviderForm.codeFrom) payload.codeFrom = this.byProviderForm.codeFrom;
      if (this.byProviderForm.codeTo) payload.codeTo = this.byProviderForm.codeTo;
      const codes = splitCsv(this.byProviderForm.productCodesCsv);
      if (codes.length) payload.productCodes = codes;

      if (this.byProviderForm.updateMethod === "percent") {
        const percent = Number(this.byProviderForm.percent);
        if (!Number.isFinite(percent) || percent < 0) {
          throw new Error("Ingresa un porcentaje válido (>= 0).");
        }
        const multiplier =
          this.byProviderForm.changeType === "decrease"
            ? 1 - percent / 100
            : 1 + percent / 100;
        if (!(multiplier > 0)) {
          throw new Error("El porcentaje de disminución no puede dejar el precio en cero o negativo.");
        }
        payload.mode = "multiplier";
        payload.value = Number(multiplier.toFixed(6));
        payload._summary = `${this.byProviderForm.changeType === "decrease" ? "disminución" : "aumento"} ${percent}%`;
        return payload;
      }

      payload.mode = "expression";
      if (this.byProviderForm.coefficientSource === "fixed") {
        const coef = Number(this.byProviderForm.fixedCoefficient);
        if (!Number.isFinite(coef) || coef <= 0) {
          throw new Error("Ingresa un coeficiente fijo válido (> 0).");
        }
        payload.expression = `COST * ${coef}`;
        payload._summary = `costo x coeficiente fijo (${coef})`;
      } else {
        payload.expression = "COST * COEF";
        payload._summary = "costo x coeficiente del artículo";
      }
      return payload;
    },
    async previewByProvider() {
      this.byProviderLoading = true;
      this.clearMessages();
      this.previewRows = [];
      try {
        const payload = this.buildByProviderPayload();
        const response = await pricingStockService.previewByProvider(payload);
        const data = this.unwrap(response) || {};
        this.previewRows = data.rows || [];
        this.byProviderSummary = `Proveedor ${data.provider ? data.provider.code : ""}: ${data.count || 0} filas (${payload._summary}).`;
        this.scrollPreviewIntoView();
      } catch (error) {
        this.onError(error);
      } finally {
        this.byProviderLoading = false;
      }
    },
    async applyByProvider() {
      this.byProviderApplyLoading = true;
      this.clearMessages();
      try {
        const payload = this.buildByProviderPayload();
        payload.description = `Actualización por proveedor (${payload._summary})`;
        delete payload._summary;
        const response = await pricingStockService.applyBulkUpdate(payload);
        const data = this.unwrap(response);
        this.success = "Actualización por proveedor aplicada.";
        this.byProviderSummary = `Aplicado. changed=${data.changed}, dependencies=${data.changedDependencies}, total=${data.totalChanged}.`;
      } catch (error) {
        this.onError(error);
      } finally {
        this.byProviderApplyLoading = false;
      }
    },
    async applyByField() {
      this.byFieldLoading = true;
      this.clearMessages();
      try {
        const payload = { priceListId: this.byFieldForm.priceListId, targetField: this.byFieldForm.targetField, operation: this.byFieldForm.operation, value: Number(this.byFieldForm.value) };
        const ids = splitCsv(this.byFieldForm.productIdsCsv);
        if (ids.length) payload.productIds = ids;
        const codes = splitCsv(this.byFieldForm.productCodesCsv);
        if (codes.length) payload.productCodes = codes;
        const response = await pricingStockService.updateByField(payload);
        const data = this.unwrap(response);
        this.byFieldResult = `Registros actualizados: ${data.updated}`;
        this.success = "Actualización por campo aplicada.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.byFieldLoading = false;
      }
    },

    async fetchMemberships() {
      this.membershipsLoading = true;
      try {
        const params = {};
        if (this.membershipFilters.priceListId) params.priceListId = this.membershipFilters.priceListId;
        if (this.membershipFilters.productCode) params.productCode = this.membershipFilters.productCode;
        const response = await pricingStockService.getMemberships(params);
        this.memberships = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.membershipsLoading = false;
      }
    },
    async upsertMemberships() {
      this.membershipUpsertLoading = true;
      this.clearMessages();
      try {
        const payload = parseJsonText(this.membershipUpsertJson);
        if (!payload) throw new Error("JSON vacío en memberships.");
        const response = await pricingStockService.upsertMemberships(payload);
        const data = this.unwrap(response);
        this.success = `Memberships procesados: ${data.processed}/${data.total}.`;
        await this.fetchMemberships();
      } catch (error) {
        this.onError(error);
      } finally {
        this.membershipUpsertLoading = false;
      }
    },
    async fetchProviderLinks() {
      this.linksLoading = true;
      try {
        const params = {};
        if (this.linkFilters.providerId) params.providerId = this.linkFilters.providerId;
        if (this.linkFilters.productCode) params.productCode = this.linkFilters.productCode;
        const response = await pricingStockService.getProviderLinks(params);
        this.providerLinks = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.linksLoading = false;
      }
    },
    async upsertLinks() {
      this.linksUpsertLoading = true;
      this.clearMessages();
      try {
        const payload = parseJsonText(this.linksUpsertJson);
        if (!payload) throw new Error("JSON vacío en links.");
        const response = await pricingStockService.upsertProviderLinks(payload);
        const data = this.unwrap(response);
        this.success = `Links procesados: ${data.processed}/${data.total}.`;
        await this.fetchProviderLinks();
      } catch (error) {
        this.onError(error);
      } finally {
        this.linksUpsertLoading = false;
      }
    },

    async runStockQuery() {
      this.stockQueryLoading = true;
      this.clearMessages();
      try {
        const payload = { includeZero: this.stockQueryForm.includeZero };
        const productCodes = splitCsv(this.stockQueryForm.productCodesCsv);
        const warehouseCodes = splitCsv(this.stockQueryForm.warehouseCodesCsv);
        if (productCodes.length) payload.productCodes = productCodes;
        if (warehouseCodes.length) payload.warehouseCodes = warehouseCodes;
        const response = await pricingStockService.bulkStockQuery(payload);
        this.stockRows = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.stockQueryLoading = false;
      }
    },
    async runStockAdjust() {
      this.stockAdjustLoading = true;
      this.clearMessages();
      try {
        const payload = parseJsonText(this.stockAdjustJson);
        if (!payload) throw new Error("JSON vacío en ajustes.");
        const response = await pricingStockService.bulkStockAdjust(payload);
        const data = this.unwrap(response);
        this.stockAdjustResult = `processed=${data.processed}, created=${data.created}, updated=${data.updated}`;
        this.success = "Ajustes de stock aplicados.";
      } catch (error) {
        this.onError(error);
      } finally {
        this.stockAdjustLoading = false;
      }
    },
    async fetchMovements() {
      this.movementsLoading = true;
      try {
        const params = { limit: 500 };
        if (this.movementFilters.productCode) params.productCode = this.movementFilters.productCode;
        if (this.movementFilters.warehouseCode) params.warehouseCode = this.movementFilters.warehouseCode;
        if (this.movementFilters.movementType) params.movementType = this.movementFilters.movementType;
        if (this.movementFilters.dateFrom) params.dateFrom = this.movementFilters.dateFrom;
        if (this.movementFilters.dateTo) params.dateTo = this.movementFilters.dateTo;
        const response = await pricingStockService.getStockMovements(params);
        this.stockMovements = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.movementsLoading = false;
      }
    },
    async fetchAuditLogs() {
      this.auditLoading = true;
      this.clearMessages();
      try {
        const params = { limit: 500 };
        if (this.auditFilters.resourceType) params.resourceType = this.auditFilters.resourceType;
        if (this.auditFilters.action) params.action = this.auditFilters.action;
        if (this.auditFilters.success !== "all") params.success = this.auditFilters.success;
        if (this.auditFilters.dateFrom) params.dateFrom = this.auditFilters.dateFrom;
        if (this.auditFilters.dateTo) params.dateTo = this.auditFilters.dateTo;
        const response = await pricingStockService.getAuditLogs(params);
        this.auditLogs = this.unwrap(response) || [];
      } catch (error) {
        this.onError(error);
      } finally {
        this.auditLoading = false;
      }
    },
    resolveImageUrl(path) {
      if (!path) return "";
      if (/^https?:\/\//i.test(path)) return path;
      if (path.startsWith("/")) {
        const envUrl = process.env.VUE_APP_API_URL;
        const baseApiUrl = /^https?:\/\//i.test(envUrl || "")
          ? envUrl
          : typeof window !== "undefined"
            ? `${window.location.origin}${envUrl && envUrl.startsWith("/") ? envUrl : "/apiv3"}`
            : `http://localhost:8129${envUrl && envUrl.startsWith("/") ? envUrl : "/apiv3"}`;
        return `${baseApiUrl}`.replace(/\/apiv3$/, "") + path;
      }
      return path;
    },
    zoomImage(item) {
      if (!item.imagePath) return;
      this.zoomImageUrl = this.resolveImageUrl(item.imagePath);
      this.zoomProductCode = item.code || '';
      this.zoomProductName = item.name || '';
      this.zoomImageDialog = true;
    },
    getMatrixColorClass(disc) {
      const idx = this.currentListMatrixDiscounts.indexOf(disc);
      const colors = [
        "teal--text text--lighten-1",
        "orange--text text--darken-2",
        "deep-orange--text accent-4",
        "red--text text--darken-2"
      ];
      return colors[idx % colors.length] || "grey--text";
    },
    formatCurrency(value) {
      const n = Number(value || 0);
      return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatNumber(value) {
      const n = Number(value || 0);
      return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    bonusLabel(item) {
      const bonus = item && item.appliedBonus;
      if (!bonus) return "";
      const value = Number(bonus.bonusValue || 0);
      return bonus.bonusType === "amount" ? `$ ${this.formatCurrency(value)}` : `${this.formatNumber(value)}%`;
    },
    formatInteger(value) {
      const n = Math.round(Number(value || 0));
      return n.toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatNullableNumber(value) {
      if (value === null || value === undefined) return "—";
      return this.formatNumber(value);
    },
    formatDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("es-AR");
    },
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("es-AR");
    },
    buildPriceListExcelHeaderRows(listDate) {
      return [
        ["herrametal s.a.", "", "herrajes y cerraduras", "", "", "", "Lista de Precios HERRAMETAL"],
        ["Florencio Varela 1236 - Lanús Oeste - (C.P. B1824ADD) - Pcia. de Bs. As."],
        ["Tel.: (011) 4241 6881 (Lineas rotativas) - Fax.: (011) 4241 1747 - Whatsapp (11) 6667 6331"],
        ["Correo electrónico: ventas@herrametalsa.com.ar - Sitio Internet: www.herrametalsa.com.ar"],
        ['Con la fecha del último cambio de precio de cada producto "fecha ultima modificacion", Ud. puede atender solo las modificaciones que no conocía.'],
        ['Se informa además porcentaje de la modificacion "%" y precio de la lista anterior "precio anterior".'],
        [],
      ];
    },
    getItemObservationsFromMaster(item) {
      const candidates = [item.masterDescription, item.shortDescription];
      for (let i = 0; i < candidates.length; i += 1) {
        const text = String(candidates[i] || "").trim();
        if (!text) continue;
        if (String(item.name || "").trim() && text.toLowerCase() === String(item.name || "").trim().toLowerCase()) continue;
        return text;
      }
      return "";
    },
    getLastRegisteredPrice(item) {
      const candidates = [
        item.lastRegisteredPrice,
        item.sourcePreviousPrice,
        item.previousPrice,
        item.lastPrice,
        item.pricePrevious,
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        const n = Number(candidates[i]);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    },
    calculatePriceChangePercent(currentPrice, lastRegisteredPrice) {
      const current = Number(currentPrice);
      const previous = Number(lastRegisteredPrice);
      if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) {
        return "";
      }
      const percent = ((current - previous) / previous) * 100;
      return Number(percent.toFixed(2));
    },
    getExportPriceChangePercent(item, currentPrice, lastRegisteredPrice) {
      const fromBackend = Number(item.priceChangePercent);
      if (Number.isFinite(fromBackend)) return Number(fromBackend.toFixed(2));
      return this.calculatePriceChangePercent(currentPrice, lastRegisteredPrice);
    },
    getExportPriceChangePercentText(item, currentPrice, lastRegisteredPrice) {
      const value = this.getExportPriceChangePercent(item, currentPrice, lastRegisteredPrice);
      const n = Number(value);
      if (!Number.isFinite(n)) return "";
      const sign = n > 0 ? "+" : "";
      const raw = Math.abs(n).toFixed(2).replace(/\.?0+$/, "");
      return `${sign}${n < 0 ? "-" : ""}${raw}%`;
    },
    selectCartDiscount(val) {
      console.log("[REALTIME SELECTION DIAGNOSTIC] selectCartDiscount clicked with:", val);
      this.cartGlobalDiscount = val;
      console.log("[REALTIME SELECTION DIAGNOSTIC] State variable updated to:", this.cartGlobalDiscount);
      this.$forceUpdate();
    },
    addToCart(item) {
      const available = Number(item.stockAvailable || 0);
      if (available <= 0) {
        this.$store.commit("ui/SHOW_NOTIFICATION", {
          text: `El artículo ${item.code} no tiene stock disponible.`,
          type: "warning"
        });
        return;
      }

      const existing = this.cart.find((x) => x.productId === item.productId);
      if (existing) {
        if (existing.quantity >= Number(existing.stockAvailable || 0)) {
          this.$store.commit("ui/SHOW_NOTIFICATION", {
            text: `No puedes agregar más de ${this.formatInteger(existing.stockAvailable)} unidad(es) para ${existing.code}.`,
            type: "warning"
          });
          return;
        }
        existing.quantity += 1;
      } else {
        this.cart.push({
          productId: item.productId,
          code: item.code,
          name: item.name,
          finalPrice: item.finalPrice,
          stockAvailable: available,
          quantity: 1,
        });
      }
      this.cartDrawer = true;
    },
    normalizeCartQuantity(item) {
      const max = Number(item.stockAvailable || 0);
      let qty = Number(item.quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) qty = 1;

      if (max <= 0) {
        item.quantity = 1;
        this.$store.commit("ui/SHOW_NOTIFICATION", {
          text: `El artículo ${item.code} quedó sin stock disponible.`,
          type: "warning"
        });
        return;
      }

      if (qty > max) {
        item.quantity = max;
        this.$store.commit("ui/SHOW_NOTIFICATION", {
          text: `Cantidad ajustada al stock disponible (${this.formatInteger(max)}) para ${item.code}.`,
          type: "warning"
        });
        return;
      }

      item.quantity = qty;
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
      if (this.cart.length === 0) this.cartDrawer = false;
    },
    async processSendToCashier() {
      if (!this.customerName || this.customerName.trim().length < 3) {
        this.customerError = "El nombre del cliente es obligatorio (mínimo 3 letras)";
        return;
      }
      this.customerError = "";
      this.clearMessages();
      this.sendingToCashier = true;

      try {
        for (let i = 0; i < this.cart.length; i += 1) {
          const line = this.cart[i];
          this.normalizeCartQuantity(line);
          if (Number(line.stockAvailable || 0) <= 0) {
            throw new Error(`El artículo ${line.code} no tiene stock disponible.`);
          }
        }

        // 1. Crear el presupuesto
        const budgetPayload = {
          customerName: this.customerName.trim(),
          taxId: this.customerTaxId.trim() || null,
          taxCondition: this.customerTaxCondition,
          email: this.customerEmail.trim() || null,
          phone: this.customerPhone.trim() || null,
          actor: this.userName,
          globalDiscountPercent: Number(this.cartGlobalDiscount || 0),
          items: this.cart.map((x) => ({
            productId: x.productId,
            quantity: x.quantity,
            unitPrice: x.finalPrice,
          })),
        };

        const createRes = await salesService.createBudget(budgetPayload);
        const orderId = createRes.data.id;
        const docNumber = createRes.data.docNumber;

        // 2. Enviar a caja (Reserva stock y cambia estado a to_pay)
        await salesService.sendToCashier(orderId, this.userName);

        this.cart = [];
        this.customerName = "";
        this.customerTaxId = "";
        this.customerTaxCondition = "Consumidor Final";
        this.customerEmail = "";
        this.customerPhone = "";
        this.customerSearchVal = null;
        this.cartGlobalDiscount = 0;
        this.cartDrawer = false;
        this.clearMessages();
        
        this.$store.commit("ui/SHOW_NOTIFICATION", {
          text: `Pedido ${docNumber} enviado a caja correctamente.`,
          type: "success"
        });
      } catch (err) {
        console.error(err);
        this.onError(err);
      } finally {
        this.sendingToCashier = false;
      }
    },
    async saveCartOrder() {
      if (!this.customerName || this.customerName.trim().length < 3) {
        this.customerError = "El nombre del cliente es obligatorio (mínimo 3 letras)";
        return;
      }
      this.savingOrder = true;
      try {
        const response = await salesService.createBudget({
          customerName: this.customerName.trim(),
          taxId: this.customerTaxId.trim() || null,
          taxCondition: this.customerTaxCondition,
          email: this.customerEmail.trim() || null,
          phone: this.customerPhone.trim() || null,
          globalDiscountPercent: Number(this.cartGlobalDiscount || 0),
          items: this.cart.map((x) => ({ productId: x.productId, quantity: x.quantity, unitPrice: x.finalPrice })),
        });
        const order = response.data?.data || response.data;
        this.cart = [];
        this.cartDrawer = false;
        this.$store.commit("ui/SHOW_NOTIFICATION", {
          text: `Pedido ${order.docNumber} guardado correctamente.`,
          type: "success"
        });
      } catch (err) {
        this.onError(err);
      } finally {
        this.savingOrder = false;
      }
    },
    onCustomerSelected(val) {
      if (!val) {
        this.customerName = "";
        return;
      }
      if (typeof val === "string") {
        this.customerName = val;
      } else {
        this.customerName = val.name || "";
        this.customerTaxId = val.taxId || "";
        this.customerTaxCondition = val.taxCondition || "Consumidor Final";
        this.customerEmail = val.email || "";
        this.customerPhone = val.phone || "";
      }
    },
    async searchCustomers(query) {
      if (!query || query.trim().length < 2) {
        this.customerSuggestions = [];
        return;
      }
      this.customerSearchLoading = true;
      try {
        const res = await api.get("/current-accounts", {
          params: { q: query, limit: 10 }
        });
        const data = res.data && res.data.data ? res.data.data : (res.data || []);
        this.customerSuggestions = data;
      } catch (err) {
        console.error("Error buscando clientes:", err);
      } finally {
        this.customerSearchLoading = false;
      }
    },
    statusLabel(status) {
      const n = Number(status);
      if (n === 0) return "0 - Baja venta";
      if (n === 1) return "1 - Activo";
      if (n === 2) return "2 - Sin reposición";
      if (n === 3) return "3 - Baja";
      return "—";
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.page-hero {
  background: var(--bg-card);
  border-radius: 12px;
  padding: 8px 16px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-sm);
}
.page-hero h2 { margin: 0 0 2px; font-size: 16px; font-weight: 700; color: var(--text-primary); }
.page-hero p { margin: 0; font-size: 12px; color: var(--text-secondary); }
.page-card {
  border-radius: 12px;
  padding: 12px 16px;
  border: 1px solid var(--border-subtle);
  background: var(--bg-card);
  box-shadow: var(--shadow-md);
}
.inner-card { border-radius: 14px; }
.active-list-row {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.actions-row { display: flex; gap: 10px; flex-wrap: wrap; }
.catalog-toolbar :deep(.v-input__slot) { border-radius: 10px; }
.catalog-download-btn {
  background: linear-gradient(135deg, #0f172a, #1e293b) !important;
  color: #f8fafc !important;
  border-radius: 12px;
  min-height: 40px;
  padding: 0 14px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  box-shadow: 0 8px 14px rgba(15, 23, 42, 0.18);
}
.catalog-download-btn:hover {
  filter: brightness(1.05);
  transform: translateY(-1px);
}
.catalog-table { border-radius: 12px; overflow: hidden; }
.catalog-table :deep(tbody td),
.catalog-table :deep(thead th) {
  height: 32px !important;
  padding: 2px 8px !important;
  font-size: 13px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important; /* Delimitador sutil en todas las columnas */
  text-align: center !important; /* Centrado total */
}
.catalog-table :deep(thead th:last-child),
.catalog-table :deep(tbody td:last-child) {
  border-right: none !important;
}
.catalog-table :deep(tbody td.text-start) {
  text-align: left !important;
}
.catalog-description {
  width: 100%;
  text-align: left;
}
.pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.pill--success { color: #0f766e; background: #ccfbf1; }
.pill--warn { color: #9a3412; background: #ffedd5; }

.page-card :deep(.v-expansion-panel-header) {
  min-height: 24px !important;
  padding: 2px 4px !important;
  font-size: 11px !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary) !important;
}
.page-card :deep(.v-expansion-panel-header__icon) {
  margin-left: 4px;
}
.page-card :deep(.v-expansion-panel-content__wrap) {
  padding: 8px 0 0 0 !important;
}
.page-card :deep(.v-expansion-panels) {
  background: transparent !important;
}
.page-card :deep(.v-expansion-panel) {
  background: transparent !important;
  margin-top: 0 !important;
}
.page-card :deep(.v-expansion-panel::before) {
  box-shadow: none !important;
}

html[data-theme="dark"] .catalog-download-btn {
  background: linear-gradient(135deg, #0f2351, #173472) !important;
  border: 1px solid rgba(148, 163, 184, 0.34);
  color: #e5edff !important;
}

html[data-theme="dark"] .page-card :deep(.v-tabs-items),
html[data-theme="dark"] .page-card :deep(.v-window-item),
html[data-theme="dark"] .page-card :deep(.v-window__container) {
  background: transparent !important;
}

html[data-theme="dark"] .page-card :deep(.v-expansion-panel),
html[data-theme="dark"] .page-card :deep(.v-expansion-panel-header),
html[data-theme="dark"] .page-card :deep(.v-expansion-panel-content__wrap) {
  background: #16233f !important;
  color: #dbeafe !important;
}

html[data-theme="dark"] .page-card :deep(.v-expansion-panel-header__icon .v-icon),
html[data-theme="dark"] .page-card :deep(.v-expansion-panel-header) {
  color: #c7d2fe !important;
}

html[data-theme="dark"] .catalog-table,
html[data-theme="dark"] .catalog-table :deep(.v-data-table__wrapper),
html[data-theme="dark"] .catalog-table :deep(table) {
  background: #16233f !important;
}

html[data-theme="dark"] .catalog-table :deep(thead th) {
  background: #1c2c4e !important;
  color: #a8bbda !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.28) !important;
}

html[data-theme="dark"] .catalog-table :deep(tbody tr) {
  background: #16233f !important;
}

html[data-theme="dark"] .catalog-table :deep(tbody tr:hover) {
  background: #1b2a4b !important;
}

html[data-theme="dark"] .catalog-table :deep(tbody td),
html[data-theme="dark"] .catalog-table :deep(.v-data-footer),
html[data-theme="dark"] .catalog-table :deep(.v-data-footer__select),
html[data-theme="dark"] .catalog-table :deep(.v-data-footer__pagination),
html[data-theme="dark"] .catalog-table :deep(.caption) {
  color: #dbeafe !important;
}

html[data-theme="dark"] .catalog-table :deep(tbody td) {
  border-bottom: 1px solid rgba(148, 163, 184, 0.18) !important;
}

html[data-theme="dark"] .catalog-table :deep(.v-icon) {
  color: #a8bbda !important;
}

html[data-theme="dark"] .inner-card {
  background: #16233f !important;
  border-color: rgba(148, 163, 184, 0.24) !important;
}

html[data-theme="dark"] .inner-card :deep(.v-label),
html[data-theme="dark"] .inner-card :deep(.v-input input),
html[data-theme="dark"] .inner-card :deep(.v-select__selection),
html[data-theme="dark"] .inner-card :deep(.v-icon),
html[data-theme="dark"] .inner-card :deep(.subtitle-1),
html[data-theme="dark"] .inner-card :deep(.subtitle-2) {
  color: #dbeafe !important;
  opacity: 1 !important;
}

html[data-theme="dark"] .inner-card :deep(.v-btn-toggle) {
  background: #1b2a4b !important;
  border: 1px solid rgba(148, 163, 184, 0.3);
}

html[data-theme="dark"] .inner-card :deep(.v-btn-toggle .v-btn) {
  background: transparent !important;
  color: #a8bbda !important;
}

html[data-theme="dark"] .inner-card :deep(.v-btn-toggle .v-btn.v-item--active),
html[data-theme="dark"] .inner-card :deep(.v-btn-toggle .v-btn.v-btn--active) {
  background: linear-gradient(135deg, #1d4ed8, #3b82f6) !important;
  color: #ffffff !important;
}

html[data-theme="dark"] .inner-card :deep(.v-btn-toggle .v-btn.v-item--active .v-btn__content),
html[data-theme="dark"] .inner-card :deep(.v-btn-toggle .v-btn.v-btn--active .v-btn__content) {
  color: #ffffff !important;
}

html[data-theme="dark"] .inner-card :deep(.v-expansion-panel),
html[data-theme="dark"] .inner-card :deep(.v-expansion-panel-header),
html[data-theme="dark"] .inner-card :deep(.v-expansion-panel-content__wrap) {
  background: #16233f !important;
  color: #dbeafe !important;
}

html[data-theme="dark"] .pill--success {
  color: #86efac;
  background: rgba(22, 163, 74, 0.2);
}

html[data-theme="dark"] .pill--warn {
  color: #fdba74;
  background: rgba(234, 88, 12, 0.22);
}
@media (max-width: 960px) {
  .page-hero { flex-direction: column; align-items: flex-start; }
  .page-card { padding: 12px; }
  .active-list-row { align-items: stretch; }
  .catalog-download-btn { width: 100%; }
}
.cart-drawer {
  display: flex;
  flex-direction: column;
}
.cart-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px; /* Espaciado general reducido para no desbordar */
}
.cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.cart-header h3 {
  margin: 0;
  font-size: 18px; /* Más estilizado y sutil */
  font-weight: 700;
  color: var(--text-primary);
}
.cart-items {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 12px;
}
.cart-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.cart-item:last-child {
  border-bottom: none;
}

/* Miniaturización del ítem horizontal */
.cart-qty-wrapper {
  width: 50px;
}
.qty-input-compact {
  width: 50px !important;
  flex: 0 0 50px !important;
}
.qty-input-compact :deep(.v-input__control) {
  min-height: 26px !important;
  height: 26px !important;
}
.qty-input-compact :deep(.v-input__slot) {
  min-height: 26px !important;
  height: 26px !important;
  padding: 0 4px !important;
  background: #fcfcfc;
}
.qty-input-compact :deep(input) {
  font-size: 11px !important;
  text-align: center;
  padding: 2px 0 !important;
  font-weight: 700;
}
.cart-item-code-nano {
  font-size: 9px !important;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  line-height: 1;
  margin-bottom: 1px;
}
.cart-item-name-nano {
  font-size: 12px !important;
  font-weight: 500;
  line-height: 1.15;
  word-break: break-word;
}
.cart-item-price-nano {
  font-size: 12px !important;
  letter-spacing: -0.2px;
}

/* COMPACTACION EXTREMA DE FORMULARIOS (INPUTS, SELECTS, BOTONES) EN EL CARRITO */
.cart-drawer :deep(.v-text-field--outlined .v-input__control),
.cart-drawer :deep(.v-select--outlined .v-input__control) {
  min-height: 32px !important;
}
.cart-drawer :deep(.v-input__slot) {
  min-height: 32px !important;
  padding: 0 8px !important;
  margin-bottom: 0px !important;
}
.cart-drawer :deep(input),
.cart-drawer :deep(.v-select__selection) {
  font-size: 12px !important;
  height: 32px !important;
}
.cart-drawer :deep(.v-label) {
  font-size: 12px !important;
  top: 6px !important;
}
.cart-drawer :deep(.v-label--active) {
  transform: translateY(-16px) scale(0.85) !important;
}
.cart-drawer :deep(.v-input__icon--append .v-icon),
.cart-drawer :deep(.v-input__icon--prepend-inner .v-icon) {
  font-size: 16px !important;
}
.cart-drawer :deep(.v-input__icon) {
  height: 24px !important;
  min-width: 24px !important;
  width: 24px !important;
}
.cart-drawer :deep(.v-btn-toggle .v-btn) {
  height: 24px !important;
  font-size: 10px !important;
  letter-spacing: 0px;
}

.empty-cart {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.cart-footer {
  padding-top: 12px; /* Footer compacto */
  border-top: 2px solid var(--border-subtle);
}

.cart-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cart-total-row span:first-child {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.total-amount {
  font-size: 24px;
  font-weight: 800;
  color: var(--primary);
}

.cart-fab {
  z-index: 10;
}

.catalog-avatar-interactive {
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease-in-out;
}
.catalog-avatar-interactive:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}
.premium-zoom-card {
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.16) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* Flujo de mostrador: el resumen de venta y la acción de caja quedan juntos. */
.cart-drawer { background: #f7f9fc; }
.cart-content { padding: 0; overflow-y: auto; }
.cart-header { margin: 0; padding: 18px 20px 14px; background: #fff; border-bottom: 1px solid #e4e9f2; }
.cart-header h3 { font-size: 20px; line-height: 1.2; letter-spacing: -0.2px; }
.cart-eyebrow { color: #2563eb; font-size: 10px; font-weight: 800; letter-spacing: .09em; margin-bottom: 4px; }
.cart-summary { color: #64748b; font-size: 12px; margin-top: 4px; }
.cart-items { flex: none; max-height: 34vh; overflow-y: auto; margin: 0; padding: 12px 16px 6px; }
.cart-item { border: 1px solid #e1e7ef; border-radius: 10px; background: #fff; padding: 10px; margin-bottom: 8px; box-shadow: 0 1px 2px rgba(15, 23, 42, .03); }
.cart-qty-wrapper, .qty-input-compact { width: 54px !important; flex-basis: 54px !important; }
.qty-input-compact :deep(.v-input__slot) { background: #f8fafc; border-radius: 7px; }
.cart-item-code-nano { font-size: 10px !important; letter-spacing: .04em; margin-bottom: 3px; }
.cart-item-name-nano { font-size: 13px !important; line-height: 1.25; }
.cart-item-price-nano { color: #172554 !important; font-size: 14px !important; white-space: nowrap; }
.cart-footer { margin: 6px 16px 16px; padding: 14px; border: 1px solid #dfe7f2; border-radius: 12px; background: #fff; }
.cart-section-title { color: #334155; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 10px; }
.cart-footer :deep(.v-text-field--outlined .v-input__control), .cart-footer :deep(.v-select--outlined .v-input__control) { min-height: 38px !important; }
.cart-footer :deep(.v-input__slot) { min-height: 38px !important; }
.cart-footer :deep(input), .cart-footer :deep(.v-select__selection) { height: 38px !important; font-size: 12px !important; }
.cart-checkout { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 14px; padding: 12px 0 10px; border-top: 1px solid #e5eaf1; }
.cart-total-label { color: #334155; font-size: 13px; font-weight: 700; }
.cart-total-hint { color: #64748b; font-size: 11px; margin-top: 2px; }
.total-amount { color: #0f172a; font-size: 25px; line-height: 1; white-space: nowrap; }
.cart-checkout-button { margin-top: 0 !important; height: 46px !important; border-radius: 9px; font-weight: 800; letter-spacing: .02em; box-shadow: 0 5px 10px rgba(22, 101, 52, .18); }
@media (max-width: 520px) { .cart-items { max-height: 30vh; } .cart-footer { margin: 6px 10px 10px; } .cart-header { padding: 16px; } }

</style>
